import { consumptionReadiness } from "../../components/config/consumptionReadiness";
import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  IconButton,
  Alert,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeviceHubIcon from "@mui/icons-material/DeviceHub";
import { useNavigate, useLocation } from "react-router-dom";
import FeasibilityShell from "./FeasibilityShell";
import PvParkSummary from "./PvParkSummary";
import { assetTypeDefinition } from "../../utils/projectAssets";
import { recommendedTransformerKva, formatKva } from "../../utils/transformerSizing";
import { useHistory } from "../../context/HistoryContext";
import { DEFAULT_CONFIG } from "../../components/config/defaultConfig";
import { getBatteryDetails, getInverterDetails, getModuleDetails } from "../../services/api";
import MaybeTooltip from "../../components/MaybeTooltip";
import InverterMpptSummary from "../../components/pv/InverterMpptSummary";
import EquipmentSpecTabs from "../../components/pv/EquipmentSpecTabs";
import DemandProjectionSection from "../../components/DemandProjectionSection";
import ConsumptionPreview from "../../components/ConsumptionPreview";
import { consumptionPeriods } from "../../components/config/consumptionPreview";
import DemandShapeSettings from "../../components/DemandShapeSettings";
import DailyConsumptionModel from "../../components/DailyConsumptionModel";
import { initialDemandShape } from "../../components/config/demandShape";
import AppliedDemandPreview from "../../components/AppliedDemandPreview";
import {
  BASE_LOAD_HELPER_TEXT,
  BASE_LOAD_LABEL,
  BASE_LOAD_TOO_HIGH_TEXT,
  baseLoadShortPeriods,
} from "../../components/config/baseLoad";
import { isBtmSettlement } from "../../components/config/btm";
import { isVirtualNetBilling } from "../../components/config/virtualNetBilling";
import VirtualNetBillingSupplies from "../../components/VirtualNetBillingSupplies";
import VirtualNetBillingSupplyTariffs from "../../components/VirtualNetBillingSupplyTariffs";
import DynamicTariffFields from "../../components/DynamicTariffFields";
import { DYNAMIC_SCHEME, DYNAMIC_SCHEME_LABEL, isDynamicScheme } from "../../components/config/dynamicTariff";
import { ITEMIZED_TARIFF_MODE, SINGLE_ENERGY_RATE, itemizedTariffRate, legacyFixedChargeErrors } from "../../components/config/retailTariff";
import { DemandProfileType } from "../../components/AssetConfigurationScope";
import { defaultDemandWeights, weightsForProfileChange } from "../../components/config/demandProfileDefaults";
import { useLanguage } from "../../context/LanguageContext";
import {
  normalizeDemandProjection,
  profileMonthlySummary,
} from "../../components/config/demandProjection";
import { MONTHS_EL_SHORT } from "../../components/config/loadProfileImport";

function normalizeChars(raw) {
  if (!raw) return {};
  if (!Array.isArray(raw)) return raw;

  const out = {};
  for (const row of raw) {
    const k = row.CharacteristicName || row.characteristic_name;
    const v = row.CharacteristicValue || row.characteristic_value;
    if (
      k !== undefined &&
      k !== null &&
      k !== "" &&
      v !== undefined &&
      v !== null &&
      v !== ""
    ) {
      out[k] = v;
    }
  }
  return out;
}

const technicalInputLabelSx = {
  color: "primary.main",
  fontWeight: 600,
  letterSpacing: "0.02em",
  fontSize: "0.95rem",
  whiteSpace: "nowrap",
  lineHeight: 1.25,
  maxWidth: "none",
  "&.Mui-focused": { color: "primary.main" },
};

// An empty outlined field draws its label inside the box, and with the theme's
// bold blue labels a row of empty fields read as a row of buttons. The tariff
// fields keep the label on the border, as a filled field has it.
const LABEL_ON_BORDER = { shrink: true };

const technicalFieldSx = {
  "& .MuiInputBase-root": { minHeight: 56 },
  "& .MuiInputBase-input": { py: 1.25 },
};

function normalizeTechnicalNumberString(raw) {
  if (raw === "" || raw == null) return "";
  const n = Number(raw);
  if (!Number.isFinite(n)) return raw;
  return n.toFixed(4).replace(/\.?0+$/, "");
}

function LabeledField({ type, value, onChange, ...props }) {
  const isNumber = type === "number";
  const [localValue, setLocalValue] = useState(
    value === undefined || value === null ? "" : String(value)
  );

  useEffect(() => {
    setLocalValue(value === undefined || value === null ? "" : String(value));
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value.replace(",", ".");
    if (isNumber && !/^\d*\.?\d*$/.test(raw)) return;
    setLocalValue(raw);
  };

  const handleBlur = () => {
    const next = isNumber ? normalizeTechnicalNumberString(localValue) : localValue;
    setLocalValue(next);
    onChange?.({ target: { value: next } });
  };

  return (
    <TextField
      {...props}
      type={isNumber ? "text" : type}
      inputMode={isNumber ? "decimal" : undefined}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      sx={technicalFieldSx}
      InputLabelProps={{ sx: technicalInputLabelSx, shrink: true }}
    />
  );
}

function ModuleDiagram({ details }) {
  if (!details) return null;
  const chars = details.characteristics || {};

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const pstc = toNum(chars.STC || chars.Impo * chars.Vmpo);
  const vmp = toNum(chars.V_mp_ref || chars.Vmpo);
  const imp = toNum(chars.I_mp_ref || chars.Impo);
  const voc = toNum(chars.V_oc_ref || chars.Voco);
  const isc = toNum(chars.I_sc_ref || chars.Isco);
  const ns = toNum(chars.N_s || chars.Cells_in_Series);
  const tNoct = toNum(chars.T_NOCT);
  const alphaScNum = toNum(chars.alpha_sc ?? chars.Aisc);
  const alphaSc = alphaScNum != null ? alphaScNum.toFixed(5) : undefined;
  const betaOc = toNum(chars.beta_oc || chars.Bvoco);
  const width = toNum(chars.Width);
  const length = toNum(chars.Length);
  const areaDb = toNum(chars.Area);
  const area = areaDb || (width && length ? width * length : undefined);
  const technology = chars.Technology || chars.Material || "—";
  const bifFlag = toNum(chars.Bifacial);
  const isBifacial = bifFlag != null ? bifFlag >= 0.5 : false;
  const bifLabel = isBifacial ? "BIFACIAL" : "MONOFACIAL";

  return (
    <Box sx={{ display: "flex", gap: 3 }}>
      <Box
        sx={{
          width: 220,
          borderRadius: 3,
          boxShadow: 4,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            p: 1.4,
          }}
        >
          <Typography
            variant="caption"
            sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
          >
            {bifLabel}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.4 }}>
            {details.name}
          </Typography>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: 2,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              P<sub>STC</sub>
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {pstc != null ? `${pstc} W` : "—"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Technology
            </Typography>
            <Typography variant="body2">{technology}</Typography>
          </Box>

          {area && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Area
              </Typography>
              <Typography variant="body2">
                {area.toFixed(2)} m²
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{ textTransform: "uppercase", fontSize: "0.8rem", mb: 0.5 }}
        >
          Electrical
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              V<sub>mp</sub>
            </Typography>
            <Typography variant="body2">{vmp != null ? `${vmp} V` : "—"}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              I<sub>mp</sub>
            </Typography>
            <Typography variant="body2">{imp != null ? `${imp} A` : "—"}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              N<sub>s</sub> (cells in series)
            </Typography>
            <Typography variant="body2">{ns != null ? ns : "—"}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              V<sub>oc</sub>
            </Typography>
            <Typography variant="body2">{voc != null ? `${voc} V` : "—"}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              I<sub>sc</sub>
            </Typography>
            <Typography variant="body2">{isc != null ? `${isc} A` : "—"}</Typography>
          </Grid>
        </Grid>

        <Typography
          variant="subtitle2"
          sx={{ textTransform: "uppercase", fontSize: "0.8rem", mb: 0.5 }}
        >
          Performance
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              NOCT
            </Typography>
            <Typography variant="body2">{tNoct != null ? `${tNoct} °C` : "—"}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              α<sub>sc</sub> (I coeff)
            </Typography>
            <Typography variant="body2">{alphaSc != null ? alphaSc : "—"}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              β<sub>oc</sub> (V coeff)
            </Typography>
            <Typography variant="body2">{betaOc != null ? betaOc : "—"}</Typography>
          </Grid>
        </Grid>

        <Typography
          variant="subtitle2"
          sx={{ textTransform: "uppercase", fontSize: "0.8rem", mb: 0.5 }}
        >
          Physical
        </Typography>
        <Grid container spacing={1.5}>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              Width
            </Typography>
            <Typography variant="body2">{width != null ? `${width} m` : "—"}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              Length
            </Typography>
            <Typography variant="body2">{length != null ? `${length} m` : "—"}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              Bifacial?
            </Typography>
            <Typography variant="body2">
              {isBifacial ? "Yes (bifacial)" : "No (monofacial)"}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

/**
 * The stringing of one inverter, drawn as an actual single-line diagram: the
 * MPPT rows on the left, a DC bus collecting them, the inverter at the end of
 * the bus. The previous version stretched two dead rules across the full card
 * width and repeated every figure twice, which is what made it read as empty.
 *
 * Defined at module scope on purpose: as a function declared inside the page's
 * render it was a new component type on every keystroke, so React threw the
 * subtree away and rebuilt it each time.
 */
function PVDesignDiagram({ inverterTitle, moduleTitle, mppts }) {
  const rows = Array.isArray(mppts) ? mppts : [];
  const totalStrings = rows.reduce((sum, m) => sum + (m.inputs || []).filter((i) => i.used).length, 0);
  const totalModules = rows.reduce((sum, m) => {
    const used = (m.inputs || []).filter((i) => i.used).length;
    const perString = Number(m.modulesPerString);
    return Number.isFinite(perString) && perString > 0 ? sum + perString * used : sum;
  }, 0);
  const last = rows.length - 1;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, backgroundColor: "background.paper" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <DeviceHubIcon fontSize="small" color="primary" />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>PV stringing diagram</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={2}>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>{totalStrings}</Box>{" "}
            <span>strings</span>
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>{totalModules.toLocaleString("en-GB")}</Box>{" "}
            <span>modules</span>
          </Typography>
        </Stack>
      </Stack>

      {rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>No strings are assigned to this inverter.</Typography>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center" }}><Box sx={{ display: "inline-flex", alignItems: "stretch", maxWidth: "100%", mx: "auto" }}>
          {/* MPPT rows and the bus that collects them */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {rows.map((mppt, idx) => {
              const inputs = mppt.inputs || [];
              const used = inputs.filter((i) => i.used).length;
              const live = used > 0;
              return (
                <Box key={idx} sx={{ display: "flex", alignItems: "stretch", height: 30 }}>
                  {/* the MPPT itself */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    sx={{ flex: 1, minWidth: 0, pr: 1.25, opacity: live ? 1 : 0.45 }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, width: 58, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
                      MPPT {idx + 1}
                    </Typography>
                    {/* one dot per DC input; filled when it carries a string */}
                    <Stack direction="row" spacing={0.4} sx={{ flexShrink: 0 }}>
                      {inputs.map((inp, j) => (
                        <Box
                          key={j}
                          title={`Input ${j + 1}`}
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            border: "1.5px solid",
                            borderColor: inp.used ? "success.main" : "text.disabled",
                            bgcolor: inp.used ? "success.main" : "transparent",
                          }}
                        />
                      ))}
                    </Stack>
                    <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {live ? (
                        <>
                          <Box component="span" sx={{ fontWeight: 700, color: "text.primary", fontVariantNumeric: "tabular-nums" }}>
                            {used}
                          </Box>
                          {" × "}
                          <Box component="span" sx={{ fontWeight: 700, color: "text.primary", fontVariantNumeric: "tabular-nums" }}>
                            {mppt.modulesPerString || "—"}
                          </Box>{" "}
                          <span>modules per string</span>
                        </>
                      ) : (
                        <span>No active strings</span>
                      )}
                    </Typography>
                  </Stack>

                  {/* the branch into the bus: a stub at this row's centre and
                      the length of bus that belongs to it */}
                  <Box sx={{ position: "relative", width: 28, flexShrink: 0 }}>
                    <Box sx={{ position: "absolute", top: "50%", left: 0, width: "50%", height: "2px", bgcolor: live ? "primary.main" : "divider" }} />
                    <Box
                      sx={{
                        position: "absolute",
                        left: "calc(50% - 1px)",
                        width: "2px",
                        bgcolor: "primary.main",
                        top: idx === 0 ? "50%" : 0,
                        bottom: idx === last ? "50%" : 0,
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* the inverter, sitting at the middle of the bus */}
          <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
            <Box sx={{ width: 22, height: "2px", bgcolor: "primary.main" }} />
            <Box
              sx={{
                px: 1.5,
                py: 1,
                borderRadius: 1.5,
                border: "1px solid",
                borderColor: "primary.main",
                bgcolor: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "primary.50"),
                maxWidth: 220,
              }}
            >
              <Typography variant="caption" sx={{ display: "block", textTransform: "uppercase", letterSpacing: 0.6, color: "text.secondary", fontWeight: 700 }}>
                Inverter
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }} title={inverterTitle}>
                {inverterTitle || "—"}
              </Typography>
              <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 0.25 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>{rows.length}</Box>{" "}
                <span>MPPT inputs</span>
              </Typography>
            </Box>
          </Stack>
        </Box></Box>
      )}

      <Typography variant="caption" sx={{ display: "block", mt: 1.5, pt: 1, borderTop: "1px dashed", borderColor: "divider", color: "text.secondary" }}>
        <span>Module</span>{" "}
        <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>{moduleTitle || "—"}</Box>
      </Typography>
    </Paper>
  );
}


// ---------- INVERTER DIAGRAM ----------

function InverterDiagram({ details }) {
  if (!details) return null;

  const chars = normalizeChars(details.characteristics);

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const pac = toNum(chars.Paco);
  const pdco = toNum(chars.Pdco);
  const vdcmax = toNum(chars.Vdcmax);
  const mpptLow = toNum(chars.Mppt_low);
  const mpptHigh = toNum(chars.Mppt_high);
  const effNom = toNum(chars.eff_nom);
  const effMax = toNum(chars.eff_max);
  const length = toNum(chars.Length);
  const width = toNum(chars.Width);

  return (
    <Box sx={{ display: "flex", gap: 3, alignItems: "stretch", flexWrap: "wrap" }}>
      {/* Left card */}
      <Box
        sx={{
          width: 230,
          borderRadius: 3,
          boxShadow: 3,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            p: 1.4,
          }}
        >
          <Typography
            variant="caption"
            sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}
          >
            INVERTER
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, mt: 0.3, lineHeight: 1.2 }}
          >
            {details.name}
          </Typography>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            p: 1.8,
            display: "flex",
            flexDirection: "column",
            gap: 1.4,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              P<sub>AC,nom</sub>
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {pac != null ? `${pac} W` : "—"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Max DC input (Pdco)
            </Typography>
            <Typography variant="body2">
              {pdco != null ? `${pdco} W` : "—"}
            </Typography>
          </Box>

          <InverterMpptSummary chars={chars} />
        </Box>
      </Box>

      {/* Right specs */}
      <Box sx={{ flex: 1 }}>
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <Typography variant="overline" sx={{ fontSize: 10 }}>
              Vdc max
            </Typography>
            <Typography variant="body2">
              {vdcmax != null ? `${vdcmax} V` : "—"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="overline" sx={{ fontSize: 10 }}>
              MPPT range
            </Typography>
            <Typography variant="body2">
              {mpptLow != null && mpptHigh != null
                ? `${mpptLow} – ${mpptHigh} V`
                : "—"}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="overline" sx={{ fontSize: 10 }}>
              Euro efficiency
            </Typography>
            <Typography variant="body2">
              {effNom != null ? `${effNom * 100 || effNom} %` : "—"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="overline" sx={{ fontSize: 10 }}>
              Max efficiency
            </Typography>
            <Typography variant="body2">
              {effMax != null ? `${effMax * 100 || effMax} %` : "—"}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="overline" sx={{ fontSize: 10 }}>
              Width
            </Typography>
            <Typography variant="body2">
              {width != null ? `${width} m` : "—"}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="overline" sx={{ fontSize: 10 }}>
              Height
            </Typography>
            <Typography variant="body2">
              {length != null ? `${length} m` : "—"}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

function extractBatterySpecs(details) {
  if (!details) return {};

  const chars = details.characteristics || {};

  const energy = details.energy ?? chars.Energy;
  const pMaxC = details.p_max_c ?? chars.P_max_c;
  const pMaxD = details.p_max_d ?? chars.P_max_d;
  const tech = chars.Technology || chars.Tech || "—";
  const vNom = chars.Vnom || chars.V_nom || chars.V_nominal;
  const vMin = chars.Vmin || chars.V_min;
  const vMax = chars.Vmax || chars.V_max;
  const effC = chars.Eff_charge;
  const effD = chars.Eff_discharge;
  const c_rate_c = chars["c-rate_c"];
  const c_rate_d = chars["c-rate_d"];
  const width = chars.Width;
  const length = chars.Length;
  const height = chars.Height;
  const weight = chars.Weight;
  const i_d_max = chars.I_disch_max;
  const soc_min = chars.soc_min;
  const soc_max = chars.soc_max;
  const price = chars.Price;
  const cycles = chars.N_cycles;

  return {
    energy,
    pMaxC,
    pMaxD,
    tech,
    vNom,
    vMin,
    vMax,
    effC,
    effD,
    c_rate_c,
    c_rate_d,
    width,
    length,
    height,
    weight,
    i_d_max,
    soc_min,
    soc_max,
    cycles,
    price,
  };
}


// --------- small component to show the “drawing” + specs in the dialog ----------
// --------- small component to show the “drawing” + specs in the dialog ----------
function BatteryDiagram({ details }) {
  if (!details) return null;

  const {
    energy,
    pMaxC,
    pMaxD,
    tech,
    vNom,
    vMin,
    vMax,
    effC,
    effD,
    c_rate_c,
    c_rate_d,
    width,
    length,
    height,
    weight,
    i_d_max,
    soc_min,
    soc_max,
    cycles,
    price,
  } = extractBatterySpecs(details);

  const fmtPercent = (x) => {
    if (x === null || x === undefined) return "—";
    const v = x <= 1 ? x * 100 : x;
    return `${v.toFixed(2)} %`;
  };

  const fmtOrDash = (x, suffix = "") =>
    x === null || x === undefined || x === "" ? "—" : `${x}${suffix}`;

  const Section = ({ title, children }) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontSize: "0.8rem",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: "text.primary",
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      <Grid container spacing={1.5}>{children}</Grid>
    </Box>
  );

  const Spec = ({ label, value }) => (
    <Grid item xs={6}>
      <Typography variant="caption" color="text.primary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Grid>
  );

  return (
    <Box sx={{ display: "flex", gap: 3 }}>
      {/* Left: battery “card” */}
      <Box
        sx={{
          width: 180,
          borderRadius: 3,
          boxShadow: 3,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            p: 1.2,
          }}
        >
          <Typography
            variant="caption"
            sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}
          >
            {tech}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.3 }}>
            {details.name}
          </Typography>
        </Box>

        {/* Body */}
        <Box
          sx={{
            flexGrow: 1,
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: 1.2,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Energy
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {energy ? `${energy} kWh` : "—"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Price
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {price ? `${price} €` : "—"}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Charge power
            </Typography>
            <Typography variant="body2">
              {fmtOrDash(pMaxC, " kW")}
            </Typography>
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary">
              Discharge power
            </Typography>
            <Typography variant="body2">
              {fmtOrDash(pMaxD, " kW")}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Right: specs grouped in sections */}
      <Box sx={{ flex: 1 }}>
        <Section title="Electrical">
          <Spec label="Nominal voltage" value={fmtOrDash(vNom, " V")} />
          <Spec
            label="Voltage range"
            value={
              vMin && vMax ? `${vMin} – ${vMax} V` : "—"
            }
          />
          <Spec label="I_discharge max" value={fmtOrDash(i_d_max, " A")} />
          <Spec
            label="c-rate charge"
            value={fmtOrDash(c_rate_c, "")}
          />
          <Spec
            label="c-rate discharge"
            value={fmtOrDash(c_rate_d, "")}
          />
        </Section>

        <Section title="Operating limits">
          <Spec label="Eff. charge" value={fmtPercent(effC)} />
          <Spec label="Eff. discharge" value={fmtPercent(effD)} />
          <Spec label="Min SoC" value={fmtOrDash(soc_min, " %")} />
          <Spec label="Max SoC" value={fmtOrDash(soc_max, " %")} />
          <Spec
            label="Cycle life"
            value={
              cycles ? `${cycles.toLocaleString("en-GB")} cycles` : "—"
            }
          />
        </Section>

        <Section title="Physical">
          <Spec label="Width" value={fmtOrDash(width, " m")} />
          <Spec label="Height" value={fmtOrDash(height, " m")} />
          <Spec label="Length" value={fmtOrDash(length, " m")} />
          <Spec label="Weight" value={fmtOrDash(weight, " kg")} />
        </Section>
      </Box>
    </Box>
  );
}


export default function FeasibilityTechnical() {
  const { isGreek } = useLanguage();
  const navigate = useNavigate();
  const history = useHistory();
  const cfg = useMemo(() => history.present ?? DEFAULT_CONFIG, [history.present]);

  const mergedCfg = useMemo(() => {
    const base = DEFAULT_CONFIG;
    const next = { ...base, ...cfg };
    next.feasibility = {
      ...base.feasibility,
      ...(cfg.feasibility || {}),
    };
    next.feasibility.technical = {
      ...base.feasibility.technical,
      ...(cfg.feasibility?.technical || {}),
    };
    next.feasibility.economics = {
      ...base.feasibility.economics,
      ...(cfg.feasibility?.economics || {}),
    };
    return next;
  }, [cfg]);

  const feasibility = mergedCfg.feasibility;
  const technical = feasibility.technical ?? {};
  const demand = technical.demand ?? {};
  const tariffs = technical.tariffs ?? {};
  const itemizedMode = tariffs.rateMode === ITEMIZED_TARIFF_MODE;
  const itemizedTariffs = tariffs.itemized || {};
  const singleEnergyRate = itemizedMode ? itemizedTariffs.pricingMode === SINGLE_ENERGY_RATE : tariffs.legacyPricingMode === SINGLE_ENERGY_RATE;
  const demandCharge = mergedCfg.btm?.demandCharge || mergedCfg.feasibility?.technical?.btm?.demandCharge || {};
  // Dynamic (hourly) tariff: the day-ahead price + fee + regulated charges
  // replace the monthly table and the peak hours (BTM only).
  const isDynamicTariff = isDynamicScheme(tariffs.billingScheme);
  const showItemizedTariff = itemizedMode && !isDynamicTariff;
  const showLegacyTariff = !itemizedMode && !isDynamicTariff;
  // The capacity charge is read from the BTM settings whichever way the per-kWh
  // tariff is entered, so it is shown under both entry modes, not only the
  // itemized one (where a total-price user never saw it).
  const capacityChargeSection = isBtmSettlement(mergedCfg.settlementType) ? (
    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: "divider" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {isGreek ? "Χρέωση ισχύος" : "Capacity charge"}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {demandCharge.enabled
          ? `${isGreek ? "Ενεργή" : "Enabled"}: ${demandCharge.measurementPeriod === "annual" ? (demandCharge.eurPerKwYear || "—") : (demandCharge.eurPerKwMonth || "—")} ${demandCharge.measurementPeriod === "annual" ? (isGreek ? "€/kW/έτος" : "EUR/kW/year") : (isGreek ? "€/kW/μήνα" : "EUR/kW/month")}`
          : (isGreek ? "Δεν έχει ενεργοποιηθεί." : "Not enabled.")}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        {isGreek
          ? "Ορίστε την πραγματική βάση χρέωσης και την τελική οικονομική τιμή ισχύος, μαζί με τυχόν μη ανακτήσιμο ΦΠΑ."
          : "Set the actual billing basis and effective capacity rate, including any non-recoverable VAT."}
      </Typography>
      <Button size="small" sx={{ mt: 0.5, px: 0 }} onClick={() => navigate("/project/asset?tab=general#btm-capacity-charge")}>
        {isGreek ? "Ρύθμιση χρέωσης ισχύος" : "Configure capacity charge"}
      </Button>
    </Box>
  ) : null;
  const legacyFixedErrors = showLegacyTariff ? legacyFixedChargeErrors(tariffs) : [];
  const demandEnabled = demand.enabled ?? true;
  const systemTypeLabel = String(mergedCfg.systemType || "Hybrid");
  const normalizedSystemType = systemTypeLabel.trim().toLowerCase();
  const isPvOnlyAsset = normalizedSystemType === "standalone pv";
  const isStandaloneBess =
    normalizedSystemType === "standalone bess" ||
    (normalizedSystemType.includes("standalone") && normalizedSystemType.includes("bess"));
  const location = useLocation();
  const requestedDemandTab = new URLSearchParams(location.search).get("section");
  const [demandTab, setDemandTab] = useState(requestedDemandTab === "tariffs" ? "tariffs" : "demand");
  useEffect(() => {
    if (!["demand", "tariffs"].includes(requestedDemandTab)) return;
    setDemandTab(requestedDemandTab);
    const frame = requestAnimationFrame(() => {
      const section = document.getElementById("consumption-tariffs");
      section?.scrollIntoView?.({ block: "start" });
      section?.focus?.({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [requestedDemandTab, location.key]);
  const [allMonthPeak, setAllMonthPeak] = useState("");
  // The same shortcut for the "total charge per kWh" table: one final price
  // typed once instead of twelve times. Kept apart from the itemized one,
  // whose figure is the energy charge before VAT, not the final price.
  const [allMonthTotalPeak, setAllMonthTotalPeak] = useState("");
  const [allMonthTotalOffPeak, setAllMonthTotalOffPeak] = useState("");
  const [allMonthOffPeak, setAllMonthOffPeak] = useState("");
  const selectedBatteries = useMemo(
    () => isPvOnlyAsset ? [] : mergedCfg.bess?.selected ?? [],
    [mergedCfg.bess?.selected, isPvOnlyAsset]
  );
  const [batteryDetailsMap, setBatteryDetailsMap] = useState({});
  const moduleName = mergedCfg.read_module || "";
  const inverterName = mergedCfg.read_inverter || "";
  const [moduleDetails, setModuleDetails] = useState(null);
  const [moduleDetailsLoading, setModuleDetailsLoading] = useState(false);
  const [moduleDetailsError, setModuleDetailsError] = useState("");
  const [inverterDetails, setInverterDetails] = useState(null);
  const [inverterDetailsLoading, setInverterDetailsLoading] = useState(false);
  const [inverterDetailsError, setInverterDetailsError] = useState("");
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [inverterDialogOpen, setInverterDialogOpen] = useState(false);
  const [batteryDialogOpen, setBatteryDialogOpen] = useState(false);
  const [batteryDialogTarget, setBatteryDialogTarget] = useState(null);
  const [inverterTab, setInverterTab] = useState(0);
  const toNum = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    const raw = String(v).replace(",", ".").trim();
    if (!raw) return null;
    const match = raw.match(/-?\d+(\.\d+)?/);
    if (!match) return null;
    const n = Number(match[0]);
    return Number.isFinite(n) ? n : null;
  };
  const normalizeSystemDesign = (systemDesign) => {
    if (!systemDesign || typeof systemDesign !== "object") return null;
    const inverterKeys = Object.keys(systemDesign);
    if (!inverterKeys.length) return null;
    const layoutByInverter = {};
    const inverterOrder = [...inverterKeys];

    inverterKeys.forEach((key) => {
      const mpptList = Array.isArray(systemDesign[key]) ? systemDesign[key] : [];
      const mpptStates = mpptList.map((mppt) => {
        const inputsRaw = Array.isArray(mppt?.inputs) ? mppt.inputs : [];
        const modulesValue =
          mppt?.modules_per_string != null
            ? mppt.modules_per_string
            : inputsRaw.find((inp) => inp?.modules_per_string != null)?.modules_per_string;
        const modulesPerString = modulesValue == null ? "" : String(modulesValue);
        const maxInputIdx = inputsRaw.reduce((max, inp) => {
          const idx = Number(inp?.index ?? inp?.inputIndex);
          return Number.isFinite(idx) && idx > max ? idx : max;
        }, 0);
        const inputsCount = Math.max(maxInputIdx, inputsRaw.length);
        const hasIndexedInputs = inputsRaw.some((inp) =>
          Number.isFinite(Number(inp?.index ?? inp?.inputIndex))
        );
        const inputs = Array.from({ length: inputsCount || 0 }, (_, i) => {
          const match = hasIndexedInputs
            ? inputsRaw.find((inp) => Number(inp?.index ?? inp?.inputIndex) === i + 1)
            : inputsRaw[i];
          const matchIndex = match?.index ?? match?.inputIndex;
          return {
            index: Number(matchIndex) || i + 1,
            used: Boolean(
              match &&
                (match.modules_per_string != null ||
                  match.modulesPerString != null ||
                  match.used === true)
            ),
            modules_per_string: match?.modules_per_string ?? match?.modulesPerString,
          };
        });
        return {
          modulesPerString,
          inputs,
        };
      });
      layoutByInverter[key] = mpptStates;
    });

    return {
      inverterOrder,
      layoutByInverter,
    };
  };
  useEffect(() => {
    let cancelled = false;
    const names = selectedBatteries
      .map((b) => String(b?.battery_name ?? b?.name ?? "").trim())
      .filter(Boolean);
    const uniqueNames = Array.from(new Set(names));
    const missing = uniqueNames.filter((name) => !batteryDetailsMap[name]);
    if (missing.length === 0) return () => {};

    async function loadDetails() {
      const results = await Promise.all(
        missing.map(async (name) => {
          try {
            const data = await getBatteryDetails(name);
            return { name, data };
          } catch {
            return null;
          }
        })
      );
      if (cancelled) return;
      setBatteryDetailsMap((prev) => {
        const next = { ...prev };
        results.forEach((item) => {
          if (item?.name && item.data) {
            next[item.name] = item.data;
          }
        });
        return next;
      });
    }

    loadDetails();
    return () => {
      cancelled = true;
    };
  }, [selectedBatteries, batteryDetailsMap]);
  useEffect(() => {
    let cancelled = false;
    async function loadModuleDetails() {
      if (!moduleName) {
        setModuleDetails(null);
        setModuleDetailsError("");
        return;
      }
      try {
        setModuleDetailsLoading(true);
        setModuleDetailsError("");
        const data = await getModuleDetails(moduleName);
        if (!cancelled) setModuleDetails(data);
      } catch (err) {
        if (!cancelled) {
          setModuleDetails(null);
          setModuleDetailsError(err?.message || "Failed to load module details.");
        }
      } finally {
        if (!cancelled) setModuleDetailsLoading(false);
      }
    }
    loadModuleDetails();
    return () => {
      cancelled = true;
    };
  }, [moduleName]);
  useEffect(() => {
    let cancelled = false;
    async function loadInverterDetails() {
      if (!inverterName) {
        setInverterDetails(null);
        setInverterDetailsError("");
        return;
      }
      try {
        setInverterDetailsLoading(true);
        setInverterDetailsError("");
        const data = await getInverterDetails(inverterName);
        if (!cancelled) setInverterDetails(data);
      } catch (err) {
        if (!cancelled) {
          setInverterDetails(null);
          setInverterDetailsError(err?.message || "Failed to load inverter details.");
        }
      } finally {
        if (!cancelled) setInverterDetailsLoading(false);
      }
    }
    loadInverterDetails();
    return () => {
      cancelled = true;
    };
  }, [inverterName]);
  const bessRows = selectedBatteries.map((b) => {
    const batteryKey = String(b?.battery_name ?? b?.name ?? "").trim();
    const details = batteryDetailsMap[batteryKey];
    const count = toNum(b?.count) ?? 1;
    const energyKwh =
      toNum(b?.energy) ??
      toNum(b?.energy_kwh) ??
      toNum(b?.energyKwh) ??
      toNum(b?.energy_kWh) ??
      toNum(b?.characteristics?.Energy) ??
      toNum(details?.energy) ??
      toNum(details?.characteristics?.Energy) ??
      null;
    const energyMwhDirect =
      toNum(b?.energy_mwh) ??
      toNum(b?.energyMwh) ??
      toNum(b?.energy_MWh) ??
      toNum(b?.characteristics?.Energy_MWh) ??
      toNum(b?.characteristics?.EnergyMWh) ??
      toNum(details?.energy_mwh) ??
      toNum(details?.energyMwh) ??
      toNum(details?.characteristics?.Energy_MWh) ??
      toNum(details?.characteristics?.EnergyMWh) ??
      null;
    const pMaxD =
      toNum(b?.p_max_d) ??
      toNum(b?.pMaxD) ??
      toNum(b?.characteristics?.P_max_d) ??
      toNum(details?.p_max_d) ??
      toNum(details?.pMaxD) ??
      toNum(details?.characteristics?.P_max_d) ??
      null;
    const pMaxC =
      toNum(b?.p_max_c) ??
      toNum(b?.pMaxC) ??
      toNum(b?.characteristics?.P_max_c) ??
      toNum(details?.p_max_c) ??
      toNum(details?.pMaxC) ??
      toNum(details?.characteristics?.P_max_c) ??
      null;
    const pMaxMwDirect =
      toNum(b?.p_max_mw) ??
      toNum(b?.pMaxMw) ??
      toNum(b?.p_max_MW) ??
      toNum(b?.characteristics?.P_max_MW) ??
      toNum(b?.characteristics?.P_max_Mw) ??
      toNum(details?.p_max_mw) ??
      toNum(details?.pMaxMw) ??
      toNum(details?.characteristics?.P_max_MW) ??
      toNum(details?.characteristics?.P_max_Mw) ??
      null;
    const powerKw = pMaxD ?? pMaxC;
    const powerMw =
      pMaxMwDirect != null ? pMaxMwDirect * count : powerKw != null ? (powerKw * count) / 1000 : null;
    const energyMwh =
      energyMwhDirect != null
        ? energyMwhDirect * count
        : energyKwh != null
        ? (energyKwh * count) / 1000
        : null;
    const displayName = b?.name ?? b?.battery_name ?? "Battery";
    return {
      key: batteryKey || displayName,
      name: displayName,
      count,
      powerMw,
      energyMwh,
    };
  });

  // What the run will size when "Recommended Rating" is on. The stored rating
  // is then a leftover from whenever it was last typed, not the answer.
  const inverterPacoKw = (() => {
    const chars = normalizeChars(inverterDetails?.characteristics);
    const w = Number(chars?.Paco);
    return Number.isFinite(w) && w > 0 ? w / 1000 : null;
  })();
  const largestBatteryKw = bessRows.reduce(
    (best, row) => (row.powerMw != null && row.powerMw * 1000 > best ? row.powerMw * 1000 : best),
    0
  );
  const transformerAuto = Boolean(mergedCfg?.useRecommendedTransformer);
  const transformerEstimate = transformerAuto
    ? recommendedTransformerKva(mergedCfg, {
        inverterPacoKw,
        batteryPowerKw: largestBatteryKw,
        hasPv: !isStandaloneBess,
      })
    : null;
  const transformerLossText = (key) => {
    const v = Number(mergedCfg?.transformerDets?.[key]);
    if (!Number.isFinite(v) || mergedCfg?.transformerDets?.[key] === "" || mergedCfg?.transformerDets?.[key] == null) return "—";
    return String(mergedCfg?.transformerDets?.lossUnits || "percent") === "kw"
      ? `${v.toLocaleString("en-GB", { maximumFractionDigits: 2 })} kW`
      : `${v.toLocaleString("en-GB", { maximumFractionDigits: 3 })} % of rated`;
  };
  // Capacities in the reader's locale, with the unit that keeps the digits
  // honest: "1.020 MW" reads as one thousand and twenty to a Greek eye, so a
  // plant under 10 MW is stated in whole kW and the grouping does the rest.
  const fmtPowerKw = (kw, unit = "W") => {
    const n = Number(kw);
    if (!Number.isFinite(n)) return "—";
    if (Math.abs(n) < 10000) return `${Math.round(n).toLocaleString("en-GB")} k${unit}`;
    return `${(n / 1000).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} M${unit}`;
  };
  const normalizedDesign = useMemo(
    () => normalizeSystemDesign(mergedCfg.systemDesign),
    [mergedCfg.systemDesign]
  );
  const inverterOrder = normalizedDesign?.inverterOrder || [];
  const inverterLayouts = normalizedDesign?.layoutByInverter || {};
  const inverterDiagrams = inverterOrder.map((key) => ({
    key,
    mppts: inverterLayouts[key] || [],
  }));
  const activeInverterIndex = Math.min(inverterTab, Math.max(inverterDiagrams.length - 1, 0));
  const activeDiagram = inverterDiagrams[activeInverterIndex] || null;
  const totalInverters = inverterDiagrams.length;
  const totalModulesPlant = inverterDiagrams.reduce((sum, inv) => {
    return (
      sum +
      inv.mppts.reduce((acc, mppt) => {
        const usedInputs = (mppt.inputs || []).filter((i) => i.used).length;
        const perString = Number(mppt.modulesPerString);
        if (!Number.isFinite(perString) || perString <= 0) return acc;
        return acc + perString * usedInputs;
      }, 0)
    );
  }, 0);
  // DC/AC, from the nameplate DC and the AC the inverters can actually pass.
  const dcAcRatio = (() => {
    const dcKw = Number(mergedCfg?.pvInstalledCap?.kw);
    const acKw = Number(inverterPacoKw) * Number(totalInverters || 0);
    return Number.isFinite(dcKw) && dcKw > 0 && Number.isFinite(acKw) && acKw > 0 ? dcKw / acKw : null;
  })();
  const batteryDialogDetails = batteryDialogTarget
    ? batteryDetailsMap[batteryDialogTarget]
    : null;

  const ComponentInfoCard = ({ label, name, onOpen }) => (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        backgroundColor: (t) =>
                  t.palette.mode === "dark"
                    ? "rgba(255,255,255,0.03)"
                    : t.palette.background.paper,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>
          {label}
        </Typography>
        <MaybeTooltip title={`Show ${label.toLowerCase()} details`}>
          <span>
            <IconButton
              size="small"
              sx={{ ml: 0.25 }}
              aria-label={isGreek
                ? (label === "Inverter" ? "Λεπτομέρειες αντιστροφέα" : "Λεπτομέρειες πλαισίου")
                : `Show ${label.toLowerCase()} details`}
              disabled={!name}
              onClick={(e) => {
                e.stopPropagation();
                onOpen?.();
              }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </span>
        </MaybeTooltip>
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {name || "—"}
      </Typography>
    </Paper>
  );
  const compactSelectSx = {
    minHeight: 40,
    "& .MuiSelect-select": { py: 1 },
  };
  const compactMenuProps = {
    PaperProps: { sx: { maxHeight: 260 } },
    anchorOrigin: { vertical: "bottom", horizontal: "left" },
    transformOrigin: { vertical: "top", horizontal: "left" },
  };
  const MONTHS = useMemo(
    () => [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    []
  );
  const scheduleHourOptions = useMemo(() => {
    const hours = ["None"];
    for (let h = 0; h < 24; h += 1) {
      hours.push(`${String(h).padStart(2, "0")}:00`);
    }
    return hours;
  }, []);
  const tariffHourOptions = useMemo(() => {
    const hours = ["None"];
    for (let h = 0; h < 24; h += 1) {
      hours.push(`${String(h).padStart(2, "0")}:00`);
    }
    return hours;
  }, []);
  const demandPeriods = useMemo(
    () => consumptionPeriods(demand.inputType || "Monthly Consumption (kWh)", isGreek),
    [demand.inputType, isGreek]
  );
  function setPathInObject(obj, path, value) {
    const keys = path.split(".");
    const clone = { ...obj };
    let cur = clone;
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i];
      cur[k] = { ...(cur[k] ?? {}) };
      cur = cur[k];
    }
    cur[keys[keys.length - 1]] = value;
    return clone;
  }

  const commit = (next) => history.commit(next);
  const setFeasibility = (path, value) => {
    commit(setPathInObject(mergedCfg, `feasibility.${path}`, value));
  };
  const setDemand = (path, value) => {
    setFeasibility(`technical.demand.${path}`, value);
  };
  const setDemandProfile = (value) => {
    const weights = weightsForProfileChange(demand.weights, mergedCfg.useCase, value);
    commit(setPathInObject({ ...mergedCfg, useCase: value }, "feasibility.technical.demand", {
      ...demand, weights, shape: initialDemandShape(value),
    }));
  };
  const scheduleApplicable = demand.shape?.version === 1
    ? demand.shape.useSchedule : true;
  const setTariffs = (path, value) => {
    setFeasibility(`technical.tariffs.${path}`, value);
  };

  const demandProjection = useMemo(() => normalizeDemandProjection(mergedCfg), [mergedCfg]);

  // Stored is not the same as in use. The summary table describes the file
  // whenever there is one; the "reference only" notices describe the engine's
  // choice, and only apply while the file is the load.
  const meteredProfileStored = Boolean(demand?.loadProfile?.values?.length);
  // Virtual net billing: the supplies are entered here, with the consumption.
  // With two or more, the project's consumption is the sum of their files and
  // the inputs for one consumption give way to the supplies.
  const vnbActive = isBtmSettlement(mergedCfg.settlementType) && isVirtualNetBilling(mergedCfg.btm);
  const vnbSupplyCount = Array.isArray(mergedCfg.btm?.virtualNetBilling?.supplies)
    ? mergedCfg.btm.virtualNetBilling.supplies.length : 0;
  const vnbOwnsLoad = vnbActive && vnbSupplyCount >= 2;
  const meteredProfileActive = meteredProfileStored && demand?.loadProfile?.use !== false;
  const [sourceWithoutFile, setSourceWithoutFile] = useState("modelled");
  const selectedLoadSource = meteredProfileStored
    ? (meteredProfileActive ? "measured" : "modelled")
    : sourceWithoutFile;
  const selectLoadSource = (source) => {
    setSourceWithoutFile(source);
    if (meteredProfileStored) {
      setDemand("loadProfile", { ...demand.loadProfile, use: source === "measured" });
    }
  };
  const updateLoadProfile = (next) => {
    if (!next) setSourceWithoutFile("modelled");
    setDemand("loadProfile", next);
  };
  const meteredSummary = useMemo(
    () => (meteredProfileStored ? profileMonthlySummary(demand.loadProfile) : null),
    [meteredProfileStored, demand?.loadProfile]
  );
  // The consumption the user typed from the bills, for the import screen to
  // compare the file against. Zero when nothing has been entered.
  const declaredAnnualKwh = useMemo(() => demandPeriods.reduce((sum, period) => {
    const raw = demand.monthly?.[period.key] ?? demand.monthly?.[period.fullKey] ?? "";
    const value = Number(String(raw).replace(",", "."));
    return sum + (Number.isFinite(value) && value > 0 ? value : 0);
  }, 0), [demandPeriods, demand.monthly]);
  const referenceOnlyText = isGreek
    ? "Μόνο για αναφορά — το εισαγόμενο προφίλ δίνει το φορτίο. Διαγράψτε το προφίλ για να μοντελοποιηθεί ξανά από αυτά τα πεδία."
    : "Reference only — the imported profile supplies the load. Clear the profile to model from these inputs again.";

  return (
    <FeasibilityShell
      title="Technical"
      manual={{ settlementType: cfg?.settlementType, section: demandTab === "tariffs" ? "tariffs" : "consumption" }}
      subtitle="Plant (PV/BESS) and project inputs are configured in their respective pages. The feasibility characteristics shown here are read-only. Demand and tariff details are set on this page."
    >
      <Stack spacing={2}>
        <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            {/* A standalone BESS has no park to summarise, so it keeps this
                block. Anything with PV is summarised once, below, in a single
                shape: headline numbers, the checks, then the detail. */}
            {isStandaloneBess && (
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                letterSpacing: "0.02em",
                fontSize: "0.95rem",
                color: "text.primary",
              }}
            >
              PV / BESS / Hybrid System Summary
            </Typography>
            )}
            {isStandaloneBess && (
              <Grid container rowSpacing={2} columnSpacing={6} alignItems="flex-start">
              <Grid item xs={12} md={isStandaloneBess ? 3 : 4}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={isStandaloneBess ? 12 : 6}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "primary.main" }}>
                      System Type
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {systemTypeLabel}
                    </Typography>
                  </Grid>
                  {!isStandaloneBess && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "primary.main" }}>
                        PV Capacity
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {fmtPowerKw(mergedCfg?.pvInstalledCap?.kw, "Wp")}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item xs={12} md={isStandaloneBess ? 9 : 8}>
                <Stack spacing={1}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ flex: "1 1 auto" }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 800, color: "primary.main", textAlign: "left" }}
                      >
                        BESS
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 220 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 800, color: "primary.main", textAlign: "left" }}
                      >
                        Transformer
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <Box sx={{ flex: "1 1 auto" }}>
                      {bessRows.length > 0 ? (
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "2.6fr 0.9fr 1.2fr 1.2fr",
                            gap: 2,
                            alignItems: "center",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1.5,
                            p: 2,
                          }}
                        >
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "primary.main" }}>
                        Battery
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "primary.main" }}>
                        Count
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "primary.main" }}>
                        Power
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "primary.main" }}>
                        Energy
                      </Typography>
                      {bessRows.map((row, idx) => (
                        <Fragment key={`${row.name}-${idx}`}>
                          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            <Typography variant="body1">{row.name}</Typography>
                            <MaybeTooltip title="Battery info" placement="top" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBatteryDialogTarget(row.key);
                                  setBatteryDialogOpen(true);
                                }}
                                sx={{ p: 0.25 }}
                              >
                                <InfoOutlinedIcon fontSize="inherit" />
                              </IconButton>
                            </MaybeTooltip>
                          </Box>
                          <Typography variant="body1">{row.count}</Typography>
                          <Typography variant="body1" sx={{ fontVariantNumeric: "tabular-nums" }}>
                            {row.powerMw != null ? fmtPowerKw(row.powerMw * 1000, "W") : "—"}
                          </Typography>
                          <Typography variant="body1" sx={{ fontVariantNumeric: "tabular-nums" }}>
                            {row.energyMwh != null ? fmtPowerKw(row.energyMwh * 1000, "Wh") : "—"}
                          </Typography>
                        </Fragment>
                      ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          No batteries selected.
                        </Typography>
                      )}
                    </Box>
                    <Paper
                      variant="outlined"
                      sx={{
                        px: 1.5,
                        py: 1.25,
                        borderRadius: 1.5,
                        minWidth: 220,
                        maxWidth: 340,
                        backgroundColor: (t) =>
                  t.palette.mode === "dark"
                    ? "rgba(255,255,255,0.03)"
                    : t.palette.background.paper,
                      }}
                    >
                      {mergedCfg?.hasTransformer ? (
                        <Box sx={{ display: "grid", gap: 0.35, mt: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Apparent rating:{" "}
                            <strong>
                              {transformerAuto
                                ? transformerEstimate?.kva != null
                                  ? `≈ ${formatKva(transformerEstimate.kva)}`
                                  : "sized at run time"
                                : formatKva(mergedCfg?.transformerDets?.rated)}
                            </strong>
                          </Typography>
                          {transformerAuto && (
                            <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.3 }}>
                              Recommended rating, sized by the run from {transformerEstimate?.note}.
                            </Typography>
                          )}
                          {String(mergedCfg?.transformerDets?.topology || "") === "per_asset" && (
                            <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.3 }}>
                              PV-block unit. Also:{" "}
                              {(mergedCfg?.transformerDets?.includeBessUnit ?? true)
                                ? `BESS block ${formatKva(mergedCfg?.transformerDets?.battery?.rated)}`
                                : "no BESS-block unit"}
                              {mergedCfg?.transformerDets?.includeHv
                                ? ` · common MV/HV ${formatKva(mergedCfg?.transformerDets?.hv?.rated)}`
                                : ""}
                              .
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            No-load loss: <strong>{transformerLossText("noLoadLoss")}</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            Load loss: <strong>{transformerLossText("loadLoss")}</strong>
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Not included
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
            )}
            {!isStandaloneBess && (
              <PvParkSummary
                cfg={mergedCfg}
                systemTypeLabel={assetTypeDefinition(mergedCfg.systemType)?.label || systemTypeLabel}
                bessRows={bessRows}
                dcAcRatio={dcAcRatio}
                transformerKva={
                  !mergedCfg?.hasTransformer
                    ? null
                    : transformerAuto
                    ? transformerEstimate?.kva != null
                      ? `≈ ${formatKva(transformerEstimate.kva)}`
                      : "sized by the run"
                    : formatKva(mergedCfg?.transformerDets?.rated)
                }
              />
            )}
            {!isStandaloneBess && (
              <Stack spacing={1.5}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "primary.main" }}>
                    PV System Design
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    flexDirection: { xs: "column", md: "row" },
                    alignItems: "stretch",
                  }}
                >
                  <Box sx={{ flex: "0 0 260px", maxWidth: 280 }}>
                    <Stack spacing={1.5}>
                      <ComponentInfoCard
                        label="Module"
                        name={moduleName}
                        onOpen={() => setModuleDialogOpen(true)}
                      />
                      <ComponentInfoCard
                        label="Inverter"
                        name={inverterName}
                        onOpen={() => setInverterDialogOpen(true)}
                      />
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          backgroundColor: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.03)"
                      : t.palette.background.paper,
                        }}
                      >
                        <Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}>
                          Plant totals
                        </Typography>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>Modules</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                            {Number(totalModulesPlant || 0).toLocaleString("en-GB")}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>Inverters</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                            {Number(totalInverters || 0).toLocaleString("en-GB")}
                          </Typography>
                        </Stack>
                      </Paper>
                    </Stack>
                  </Box>
                  <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                    {inverterDiagrams.length > 0 ? (
                      <Box sx={{ display: "grid", gap: 1.5 }}>
                        <Tabs
                          value={activeInverterIndex}
                          onChange={(e, v) => setInverterTab(v)}
                          variant="scrollable"
                          scrollButtons="auto"
                          textColor="inherit"
                          indicatorColor="primary"
                          sx={{
                            minHeight: 36,
                            maxWidth: "100%",
                            "& .MuiTabs-flexContainer": { flexWrap: "nowrap" },
                            "& .MuiTab-root": {
                              minHeight: 36,
                              textTransform: "none",
                              fontWeight: 600,
                              minWidth: 140,
                            },
                          }}
                        >
                          {inverterDiagrams.map((inv, idx) => (
                            <Tab key={inv.key} label={inv.key || `Inverter ${idx + 1}`} />
                          ))}
                        </Tabs>
                        <PVDesignDiagram
                          inverterTitle={inverterName || activeDiagram?.key}
                          moduleTitle={moduleName}
                          mppts={activeDiagram?.mppts || []}
                        />
                      </Box>
                    ) : (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          color: "text.secondary",
                          background:
                            "linear-gradient(135deg, rgba(2, 32, 71, 0.04), rgba(2, 32, 71, 0.08))",
                        }}
                      >
                        <Typography variant="body2">
                          No PV system design has been saved yet.
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                </Box>
              </Stack>
            )}
          </Stack>
        </Paper>

        <Paper id="consumption-tariffs" tabIndex={-1} elevation={2} sx={{ p: 2, borderRadius: 2, scrollMarginTop: "80px" }}>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                  fontSize: "0.95rem",
                  color: "text.primary",
                }}
              >
                Demand & Tariffs
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(demandEnabled)}
                    onChange={(e) => setDemand("enabled", e.target.checked)}
                  />
                }
                label="Include Demand"
              />
            </Box>
          </Stack>
        </Paper>

        {demandEnabled && (
          <Paper elevation={2} sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={2}>
              <Tabs
                value={demandTab}
                onChange={(e, v) => setDemandTab(v)}
                textColor="inherit"
                indicatorColor="primary"
                sx={{
                  "& .MuiTabs-flexContainer": { gap: 1 },
                  "& .MuiTabs-indicator": { height: 0 },
                }}
              >
                {[
                  { value: "demand", label: isGreek ? "Ζήτηση" : "Demand" },
                  { value: "tariffs", label: "Tariffs" },
                ].map((tab) => {
                  const isActive = demandTab === tab.value;
                  return (
                    <Tab
                      key={tab.value}
                      value={tab.value}
                      label={tab.label}
                      disableRipple
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 1.2,
                        minHeight: 36,
                        px: 2,
                        border: "1px solid",
                        borderColor: isActive ? "primary.main" : "divider",
                        color: isActive ? "primary.contrastText" : "text.secondary",
                        backgroundColor: isActive ? "primary.main" : "background.paper",
                        boxShadow: isActive ? "0 6px 14px rgba(0,0,0,0.18)" : "none",
                        transition: "all 150ms ease",
                        "&:hover": {
                          boxShadow: "0 6px 14px rgba(0,0,0,0.18)",
                          backgroundColor: isActive ? "primary.main" : "action.hover",
                          color: isActive ? "primary.contrastText" : "text.primary",
                        },
                      }}
                    />
                  );
                })}
              </Tabs>
              <Grid container spacing={2}>
                {demandTab === "demand" && (
                  <Grid size={12} sx={{ minWidth: 0 }}>
                    <Box>
              <Stack spacing={2}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                    fontSize: "0.95rem",
                    color: "text.primary",
                  }}
                >
                  {isGreek ? "Κατανάλωση ηλεκτρικής ενέργειας" : "Electricity consumption"}
                </Typography>
                {vnbActive && <VirtualNetBillingSupplies cfg={mergedCfg} onChange={commit} />}
                {!vnbOwnsLoad && (<>
                {consumptionReadiness({ ...demand, enabled: true }) === "missing" && <Alert severity="info" sx={{ mb: 2 }}>
                  {isGreek ? "Για τη διαστασιολόγηση χρειάζονται δεδομένα κατανάλωσης. Το πρότυπο ορίζει μόνο την κατανομή: συμπληρώστε τις kWh ανά περίοδο ή εισαγάγετε αρχείο μετρήσεων."
                    : "PV sizing needs consumption data. The template defines only its distribution: enter kWh per period or import measurements."}
                </Alert>}
                <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
                  {isGreek
                    ? "Επιλέξτε πώς θα οριστεί η κατανάλωση. Η μελέτη χρησιμοποιεί μία πηγή κάθε φορά."
                    : "Choose how to define consumption. The study uses one source at a time."}
                </Typography>
                <Box role="radiogroup" aria-label={isGreek ? "Τρόπος καθορισμού κατανάλωσης" : "Consumption source"}
                  sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 }}>
                  {[
                    {
                      value: "modelled",
                      title: isGreek ? "Μοντελοποίηση κατανάλωσης" : "Model consumption",
                      detail: isGreek
                        ? "Από kWh ανά περίοδο, ωράριο και σχήμα φορτίου."
                        : "From consumption by period, operating hours and load shape.",
                    },
                    {
                      value: "measured",
                      title: isGreek ? "Εισαγωγή αρχείου μετρημένης κατανάλωσης" : "Import measured consumption file",
                      detail: isGreek
                        ? "Αρχείο CSV με ωριαίες τιμές ή τιμές 15 λεπτών."
                        : "CSV file with hourly or 15-minute values.",
                    },
                  ].map((source) => (
                    <Box key={source.value} component="label"
                      sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, p: 1.5,
                        border: "1px solid", borderColor: selectedLoadSource === source.value ? "primary.main" : "divider",
                        bgcolor: selectedLoadSource === source.value ? "action.selected" : "background.paper",
                        borderRadius: 1.5, cursor: "pointer" }}>
                      <Radio checked={selectedLoadSource === source.value}
                        onChange={() => selectLoadSource(source.value)}
                        value={source.value} name="consumption-source" size="small"
                        inputProps={{ "aria-label": source.title }} sx={{ p: 0.25 }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{source.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{source.detail}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
                {selectedLoadSource === "measured" && (
                  <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.5 }}>
                    {!meteredProfileStored && (
                      <Alert severity="info" sx={{ mb: 1 }}>
                        {isGreek
                          ? "Εισάγετε αρχείο για να ενεργοποιηθεί αυτή η μέθοδος. Ως τότε η μελέτη χρησιμοποιεί τη μοντελοποίηση."
                          : "Import a file to activate this method. Until then, the study uses the modelled consumption."}
                      </Alert>
                    )}
                    <DemandProjectionSection section="import" showSourceSwitch={false}
                      projection={demandProjection}
                      onChange={(next) => setFeasibility("technical.demandProjection", next)}
                      loadProfile={demand.loadProfile}
                      onLoadProfileChange={updateLoadProfile}
                      declaredAnnualKwh={declaredAnnualKwh}
                      cfg={mergedCfg} />
                  </Box>
                )}
                {selectedLoadSource === "modelled" && (
                <>
                {meteredProfileStored && (
                  <Alert severity="info">
                    {isGreek
                      ? "Το μετρημένο αρχείο παραμένει αποθηκευμένο, αλλά δεν χρησιμοποιείται. Μπορείτε να το επιλέξετε ξανά χωρίς νέα εισαγωγή."
                      : "The measured file remains saved but is not used. You can select it again without re-importing it."}
                  </Alert>
                )}
                <FormControl fullWidth>
                  <InputLabel id="consumption-period-label" sx={technicalInputLabelSx}>{isGreek ? "Περίοδος καταχώρισης κατανάλωσης" : "Consumption entry period"}</InputLabel>
                    <Select
                      labelId="consumption-period-label"
                      id="consumption-period"
                      label={isGreek ? "Περίοδος καταχώρισης κατανάλωσης" : "Consumption entry period"}
                      value={demand.inputType || "Monthly Consumption (kWh)"}
                      onChange={(e) => setDemand("inputType", e.target.value)}
                      sx={{ minHeight: 56 }}
                    >
                      <MenuItem value="Monthly Consumption (kWh)">
                        {isGreek ? "Μηνιαία κατανάλωση (kWh)" : "Monthly consumption (kWh)"}
                      </MenuItem>
                      <MenuItem value="Bi-Monthly Consumption (kWh)">
                        {isGreek ? "Διμηνιαία κατανάλωση (kWh)" : "Two-month consumption (kWh)"}
                      </MenuItem>
                      <MenuItem value="Quarterly Consumption (kWh)">
                        {isGreek ? "Τριμηνιαία κατανάλωση (kWh)" : "Quarterly consumption (kWh)"}
                      </MenuItem>
                      <MenuItem value="Annual Consumption (kWh)">
                        {isGreek ? "Ετήσια κατανάλωση (kWh)" : "Annual consumption (kWh)"}
                      </MenuItem>
                    </Select>
                </FormControl>

                <Grid container spacing={2}>
                  <Grid size={12} sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary", borderLeft: "4px solid", borderColor: "primary.main", pl: 1.25, mb: 1.5 }}>
                      {isGreek ? "Κατανάλωση ηλεκτρικής ενέργειας σε kWh" : "Electricity consumption in kWh"}
                    </Typography>
                    {meteredProfileActive && (
                      <Typography
                        variant="caption"
                        sx={{ display: "block", mb: 1, color: "info.main" }}
                      >
                        {referenceOnlyText}
                      </Typography>
                    )}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
                        columnGap: 2,
                        rowGap: 1,
                        opacity: meteredProfileActive ? 0.5 : 1,
                      }}
                    >
                        {demandPeriods.map((period) => (
                          <Box
                            key={period.key}
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "minmax(0, 1fr)",
                              gap: 0.5,
                              alignContent: "start",
                            }}
                          >
                            <Typography id={`consumption-${period.key}-label`} data-no-translate="" variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                              {period.label}
                            </Typography>
                            <LabeledField
                              id={`consumption-${period.key}`}
                              inputProps={{ "aria-labelledby": `consumption-${period.key}-label`, min: 0, step: "any" }}
                              InputProps={{ endAdornment: <InputAdornment position="end">kWh</InputAdornment> }}
                              type="number"
                              value={
                                demand.monthly?.[period.key] ??
                                demand.monthly?.[period.fullKey] ??
                                ""
                              }
                              onChange={(e) => {
                                const raw = e.target.value.replace(",", ".");
                                if (!/^\d*\.?\d*$/.test(raw)) return;
                                setDemand(`monthly.${period.key}`, raw);
                              }}
                              disabled={meteredProfileActive}
                              fullWidth
                            />
                          </Box>
                        ))}
                    </Box>

                    {/* Behind the meter: a constant standby load under the modelled
                        profile (backend demand_shape.apply_base_load). Off with an
                        imported profile, like every other synthesis input here. */}
                    {isBtmSettlement(mergedCfg.settlementType) && (
                      <Box sx={{ mt: 2, opacity: meteredProfileActive ? 0.5 : 1 }}>
                        <LabeledField
                          type="number"
                          label={BASE_LOAD_LABEL}
                          value={demand.baseLoadKw ?? ""}
                          onChange={(e) => setDemand("baseLoadKw", e.target.value)}
                          disabled={meteredProfileActive}
                          helperText={BASE_LOAD_HELPER_TEXT}
                          fullWidth
                        />
                        {!meteredProfileActive && (() => {
                          const short = baseLoadShortPeriods(
                            demand.baseLoadKw,
                            demandPeriods,
                            (period) => demand.monthly?.[period.key] ?? demand.monthly?.[period.fullKey]
                          );
                          return short.length ? (
                            <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "warning.dark" }}>
                              <span>{BASE_LOAD_TOO_HIGH_TEXT}</span>{" "}
                              {short.map((label, index) => (
                                <Fragment key={label}>
                                  {index ? ", " : null}
                                  <span>{label}</span>
                                </Fragment>
                              ))}
                            </Typography>
                          ) : null;
                        })()}
                      </Box>
                    )}

                    {/* What the imported file actually contains, shown beside the
                        inputs it replaces rather than written into them. */}
                    {meteredSummary && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1.5,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {isGreek ? "Από το μετρημένο αρχείο" : "From the imported profile"}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mb: 1, color: "text.secondary" }}
                        >
                          {isGreek
                            ? `${Math.round(meteredSummary.annualKwh).toLocaleString("en-US")} kWh/έτος · αιχμή ${Math.round(meteredSummary.peakKw).toLocaleString("en-US")} kW · συντελεστής φορτίου ${meteredSummary.loadFactorPct.toFixed(1)}%`
                            : `${Math.round(meteredSummary.annualKwh).toLocaleString("en-US")} kWh/year · peak ${Math.round(meteredSummary.peakKw).toLocaleString("en-US")} kW · load factor ${meteredSummary.loadFactorPct.toFixed(1)}%`}
                          {meteredSummary.partial && (isGreek
                            ? " · το αρχείο δεν καλύπτει ακριβώς ένα έτος· θα προσαρμοστεί"
                            : " · file does not cover a whole year, it will be trimmed or extended")}
                        </Typography>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            columnGap: 1.5,
                          }}
                        >
                          {meteredSummary.perMonth.map((row, monthIndex) => (
                            <Box
                              key={row.month}
                              sx={{ display: "flex", justifyContent: "space-between" }}
                            >
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {isGreek ? MONTHS_EL_SHORT[monthIndex] : row.month.slice(0, 3)}
                              </Typography>
                              <Typography variant="caption" sx={{ fontVariantNumeric: "tabular-nums" }}>
                                {Math.round(row.kwh).toLocaleString("en-US")}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", mt: 1, color: "text.secondary" }}
                        >
                          {isGreek
                            ? "Συγκρίνετε με τους λογαριασμούς. Μεγάλη διαφορά σημαίνει συνήθως λάθος μονάδα, δεκαδικά ή παροχή."
                            : "Compare these against the site's bills. A large mismatch usually means the file is in the wrong unit or covers a different year."}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ mt: 2 }}>
                      <ConsumptionPreview periods={demandPeriods} demand={demand} />
                    </Box>
                  </Grid>
                  <Grid size={12} sx={{ minWidth: 0 }}>
                    <Stack spacing={2}>
                      <DemandProfileType config={mergedCfg} onChange={setDemandProfile} />
                      <DemandShapeSettings demand={demand} type={mergedCfg.useCase || "Commercial"}
                        metered={meteredProfileActive} onChange={(shape) => setDemand("shape", shape)}
                        onApplyPreset={() => {
                          commit(setPathInObject(mergedCfg, "feasibility.technical.demand", {
                            ...demand, weights: defaultDemandWeights(mergedCfg.useCase),
                            shape: initialDemandShape(mergedCfg.useCase),
                          }));
                        }} scheduleContent={
                      <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary", borderLeft: "4px solid", borderColor: "primary.main", pl: 1.25 }}>
                              {isGreek ? "Ωράριο λειτουργίας" : "Operating schedule"}
                            </Typography>
                            {meteredProfileActive && (
                              <Typography
                                variant="caption"
                                sx={{ ml: 1, color: "info.main" }}
                              >
                                reference only
                              </Typography>
                            )}
                            <Tooltip
                              title={
                                isGreek ? "Ορίστε τις ώρες λειτουργίας ανά τύπο ημέρας. Υποστηρίζονται και νυχτερινά ωράρια, π.χ. 22:00–06:00. Ο τρόπος κατανομής παραπάνω καθορίζει πώς χρησιμοποιείται το ωράριο." : "Set operating hours for each day type. Overnight windows such as 22:00–06:00 are supported. The allocation method above determines how these hours are used."
                              }
                              arrow
                            >
                              <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            </Tooltip>
                          </Box>
                          {!scheduleApplicable && (
                            <Typography
                              variant="caption"
                              sx={{ display: "block", mb: 1, color: "warning.main" }}
                            >
                              {isGreek
                                ? "Η χρήση ωραρίου είναι απενεργοποιημένη. Το παρακάτω ωράριο δεν επηρεάζει τον υπολογισμό."
                                : "Operating hours are disabled. The schedule below does not affect the calculation."}
                            </Typography>
                          )}
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Weekdays
                                </Typography>
                                <Tooltip
                                  title="Specify the start and end time of operating hours on weekdays."
                                  arrow
                                >
                                  <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                                </Tooltip>
                              </Box>
                            <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                              <InputLabel sx={technicalInputLabelSx}>Start Hour</InputLabel>
                              <Select
                                label="Start Hour"
                                value={demand.schedule?.weekdays?.startHour || "None"}
                                onChange={(e) =>
                                  setDemand("schedule.weekdays.startHour", e.target.value)
                                }
                                disabled={meteredProfileActive || !scheduleApplicable}
                                size="small"
                                sx={compactSelectSx}
                                MenuProps={compactMenuProps}
                              >
                                {scheduleHourOptions.map((hour) => (
                                  <MenuItem key={hour} value={hour}>
                                    {hour}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <FormControl fullWidth>
                              <InputLabel sx={technicalInputLabelSx}>End Hour</InputLabel>
                              <Select
                                label="End Hour"
                                value={demand.schedule?.weekdays?.endHour || "None"}
                                onChange={(e) =>
                                  setDemand("schedule.weekdays.endHour", e.target.value)
                                }
                                disabled={meteredProfileActive || !scheduleApplicable}
                                size="small"
                                sx={compactSelectSx}
                                MenuProps={compactMenuProps}
                              >
                                {(demand.shape?.version === 1 ? [...scheduleHourOptions, "24:00"] : scheduleHourOptions).map((hour) => (
                                  <MenuItem key={hour} value={hour}>
                                    {hour}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Saturdays
                                </Typography>
                                <Tooltip
                                  title={
                                    "Specify the start time of operating hours on Saturdays. Empty fields indicate the business is closed."
                                  }
                                  arrow
                                >
                                  <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                                </Tooltip>
                              </Box>
                            <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                              <InputLabel sx={technicalInputLabelSx}>Start Hour</InputLabel>
                              <Select
                                label="Start Hour"
                                value={demand.schedule?.saturdays?.startHour || "None"}
                                onChange={(e) =>
                                  setDemand("schedule.saturdays.startHour", e.target.value)
                                }
                                disabled={meteredProfileActive || !scheduleApplicable}
                                size="small"
                                sx={compactSelectSx}
                                MenuProps={compactMenuProps}
                              >
                                {scheduleHourOptions.map((hour) => (
                                  <MenuItem key={hour} value={hour}>
                                    {hour}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <FormControl fullWidth>
                              <InputLabel sx={technicalInputLabelSx}>End Hour</InputLabel>
                              <Select
                                label="End Hour"
                                value={demand.schedule?.saturdays?.endHour || "None"}
                                onChange={(e) =>
                                  setDemand("schedule.saturdays.endHour", e.target.value)
                                }
                                disabled={meteredProfileActive || !scheduleApplicable}
                                size="small"
                                sx={compactSelectSx}
                                MenuProps={compactMenuProps}
                              >
                                {(demand.shape?.version === 1 ? [...scheduleHourOptions, "24:00"] : scheduleHourOptions).map((hour) => (
                                  <MenuItem key={hour} value={hour}>
                                    {hour}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 4 }} sx={{ minWidth: 0 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Sundays
                                </Typography>
                                <Tooltip
                                  title={
                                    "Specify the start time of operating hours on Sundays. Empty fields indicate the business is closed."
                                  }
                                  arrow
                                >
                                  <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                                </Tooltip>
                              </Box>
                            <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                              <InputLabel sx={technicalInputLabelSx}>Start Hour</InputLabel>
                              <Select
                                label="Start Hour"
                                value={demand.schedule?.sundays?.startHour || "None"}
                                onChange={(e) =>
                                  setDemand("schedule.sundays.startHour", e.target.value)
                                }
                                disabled={meteredProfileActive || !scheduleApplicable}
                                size="small"
                                sx={compactSelectSx}
                                MenuProps={compactMenuProps}
                              >
                                {scheduleHourOptions.map((hour) => (
                                  <MenuItem key={hour} value={hour}>
                                    {hour}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <FormControl fullWidth>
                              <InputLabel sx={technicalInputLabelSx}>End Hour</InputLabel>
                              <Select
                                label="End Hour"
                                value={demand.schedule?.sundays?.endHour || "None"}
                                onChange={(e) =>
                                  setDemand("schedule.sundays.endHour", e.target.value)
                                }
                                disabled={meteredProfileActive || !scheduleApplicable}
                                size="small"
                                sx={compactSelectSx}
                                MenuProps={compactMenuProps}
                              >
                                {(demand.shape?.version === 1 ? [...scheduleHourOptions, "24:00"] : scheduleHourOptions).map((hour) => (
                                  <MenuItem key={hour} value={hour}>
                                    {hour}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Box>

                    } />
                    </Stack>
                  </Grid>
                </Grid>
                </>
                )}
                {selectedLoadSource === "modelled" && (
                  <>
                  <DailyConsumptionModel demand={demand} useCase={mergedCfg.useCase}
                    disabled={meteredProfileActive} onChange={(shape) => setDemand("shape", shape)} />
                  <AppliedDemandPreview demand={demand} useCase={mergedCfg.useCase}
                    year={feasibility.simulationYear} includeBaseLoad={isBtmSettlement(mergedCfg.settlementType)} />
                  </>
                )}
                </>)}
                <DemandProjectionSection section="projection"
                  projection={demandProjection}
                  onChange={(next) => setFeasibility("technical.demandProjection", next)}
                  loadProfile={demand.loadProfile}
                  onLoadProfileChange={updateLoadProfile}
                  cfg={mergedCfg} />
              </Stack>
            </Box>
          </Grid>
        )}
        {demandTab === "tariffs" && (
          <Grid size={12} sx={{ width: "100%" }}>
            <Box>
              <Stack spacing={2}>
                {vnbActive && <VirtualNetBillingSupplyTariffs cfg={mergedCfg} onChange={commit} />}
                {!isDynamicTariff && (
                  <Box sx={{ maxWidth: 540 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>{isGreek ? "Τρόπος καταχώρισης τιμολογίου" : "Tariff entry mode"}</InputLabel>
                      <Select
                        label={isGreek ? "Τρόπος καταχώρισης τιμολογίου" : "Tariff entry mode"}
                        value={tariffs.rateMode || "legacy_total"}
                        onChange={(e) => {
                          const rateMode = e.target.value;
                          const pricingMode = singleEnergyRate ? SINGLE_ENERGY_RATE : "dual";
                          setFeasibility("technical.tariffs", {
                            ...tariffs,
                            rateMode,
                            legacyPricingMode: pricingMode,
                            itemized: { ...tariffs.itemized, pricingMode },
                            ...(singleEnergyRate && rateMode === "legacy_total" ? {
                              billingScheme: "Single zonal",
                              single: { ...tariffs.single, peakStart: "00:00", peakEnd: "23:00" },
                              monthly: Object.fromEntries(MONTHS.map(month => [month, {
                                ...tariffs.monthly?.[month],
                                peak: tariffs.monthly?.[month]?.peak ?? "",
                                offPeak: tariffs.monthly?.[month]?.peak ?? "",
                              }])),
                            } : {}),
                          });
                        }}
                      >
                        <MenuItem value="legacy_total">{isGreek ? "Συνολική χρέωση ανά kWh" : "Total effective price per kWh"}</MenuItem>
                        <MenuItem value={ITEMIZED_TARIFF_MODE}>{isGreek ? "Χρέωση ενέργειας + λοιπές ανά kWh" : "Energy charge + other per-kWh charges"}</MenuItem>
                      </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }} data-testid="tariff-entry-mode-help">
                      {(tariffs.rateMode || "legacy_total") === "legacy_total"
                        ? (isGreek
                          ? "Δηλώνετε την τελική τιμή ανά kWh του λογαριασμού: ενέργεια, χρεώσεις δικτύου, ΥΚΩ και λοιπές ρυθμιζόμενες χρεώσεις, μαζί με τον ΦΠΑ που δεν ανακτάται. Επιχείρηση που ανακτά τον ΦΠΑ δηλώνει την τιμή χωρίς ΦΠΑ."
                          : "Enter the bill's final price per kWh: energy, network charges, PSO and other regulated charges, with any VAT that is not recovered. A business that recovers VAT enters the price without VAT.")
                        : (isGreek
                          ? "Δηλώνετε χωριστά, προ ΦΠΑ, τη χρέωση ενέργειας, τις λοιπές χρεώσεις ανά kWh και την ΥΚΩ, και τον συντελεστή ΦΠΑ· η τελική τιμή ανά kWh υπολογίζεται στον πίνακα."
                          : "Enter the energy charge, other per-kWh charges and PSO separately, before VAT, with the VAT rate; the final price per kWh is worked out in the table.")}
                    </Typography>
                  </Box>
                )}
                {!isDynamicTariff && (
                    <FormControl size="small" sx={{ width: { xs: "100%", sm: 300 } }}>
                      <InputLabel id="tariff-energy-rates-label">{isGreek ? "Τιμές ενέργειας" : "Energy rates"}</InputLabel>
                      <Select
                        labelId="tariff-energy-rates-label"
                        label={isGreek ? "Τιμές ενέργειας" : "Energy rates"}
                        value={singleEnergyRate ? SINGLE_ENERGY_RATE : "dual"}
                        onChange={(e) => {
                          if (showItemizedTariff) {
                            setTariffs("itemized.pricingMode", e.target.value);
                          } else {
                            const single = e.target.value === SINGLE_ENERGY_RATE;
                            setFeasibility("technical.tariffs", {
                              ...tariffs, legacyPricingMode: e.target.value,
                              ...(single ? {
                                billingScheme: "Single zonal",
                                single: { ...tariffs.single, peakStart: "00:00", peakEnd: "23:00" },
                                monthly: Object.fromEntries(MONTHS.map(month => [month, {
                                  ...tariffs.monthly?.[month],
                                  peak: tariffs.monthly?.[month]?.peak ?? "",
                                  offPeak: tariffs.monthly?.[month]?.peak ?? "",
                                }])),
                              } : {}),
                            });
                          }
                        }}
                      >
                        <MenuItem value={SINGLE_ENERGY_RATE}>{isGreek ? "Μία τιμή όλη την ημέρα" : "One rate all day"}</MenuItem>
                        <MenuItem value="dual">{isGreek ? "Αιχμή και εκτός αιχμής" : "Peak and off-peak"}</MenuItem>
                      </Select>
                      {!isDynamicTariff && <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {singleEnergyRate
                          ? (isGreek ? "Μία τιμή για όλες τις ώρες κάθε μήνα." : "One rate for every hour of each month.")
                          : (isGreek ? "Η τιμή αιχμής ισχύει στις ώρες αιχμής του ωραρίου, η τιμή εκτός αιχμής στις υπόλοιπες." : "The peak rate applies in the schedule's peak hours, the off-peak rate in all other hours.")}
                      </Typography>}
                    </FormControl>
                )}
                <Grid container spacing={2} sx={{
                  display: "grid",
                  width: "100%",
                  gridTemplateColumns: {
                    xs: "minmax(0, 1fr)",
                    lg: isDynamicTariff
                      ? "minmax(0, 2fr) minmax(300px, 1fr)"
                      : `minmax(0, ${showItemizedTariff ? (singleEnergyRate ? 560 : 840) : (singleEnergyRate ? 360 : 560)}px) minmax(0, 400px)`,
                  },
                  alignItems: "start",
                  justifyContent: "start",
                  "& > .MuiGrid-root": { width: "100%", minWidth: 0 },
                }}>
                {showItemizedTariff && (
                  <Grid item xs={12} md={8} sx={{ width: "100%", maxWidth: 840, minWidth: 0, flex: "0 1 840px" }}>
                    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                        {isGreek ? "Μεταβλητές χρεώσεις ανά kWh και ΦΠΑ" : "Variable per-kWh charges and VAT"}
                      </Typography>
                      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5 }}>
                        {[
                          ["regulatedEurKwh", isGreek ? "Λοιπές χρεώσεις (€/kWh)" : "Other charges (EUR/kWh)"],
                          ["vatRatePct", isGreek ? "ΦΠΑ ρεύματος (%)" : "Electricity VAT (%)"],
                          ["vatRecoverablePct", isGreek ? "Ανακτήσιμος ΦΠΑ (%)" : "Recoverable VAT (%)"],
                        ].map(([key, label]) => (
                          <TextField
                            key={key}
                            fullWidth
                            size="small"
                            label={label}
                            type="number"
                            value={itemizedTariffs[key] ?? ""}
                            onChange={(e) => setTariffs(`itemized.${key}`, e.target.value)}
                            InputLabelProps={LABEL_ON_BORDER}
                            helperText={key === "vatRecoverablePct"
                              ? (isGreek ? "Κενό = 0%: ο ΦΠΑ δεν ανακτάται. 100%: πλήρης ανάκτηση." : "Blank = 0%: VAT is not recoverable. 100%: fully recoverable.")
                              : undefined}
                            inputProps={{ min: 0, max: key.endsWith("Pct") ? 100 : undefined, step: "any" }}
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                        {isGreek
                          ? "Η χρέωση ενέργειας και οι λοιπές χρεώσεις €/kWh καταχωρίζονται πριν από ΦΠΑ. Στις λοιπές βάλτε μόνο ποσά που μειώνονται μαζί με τις απορροφώμενες kWh και έχουν τον ίδιο συντελεστή ΦΠΑ. Μην προσθέσετε εδώ τις ΥΚΩ ή τα πάγια."
                          : "Enter the energy charge and other per-kWh charges before VAT. Include only charges that fall with imported kWh and share the same VAT rate. Enter PSO and fixed fees separately."}
                      </Typography>
                      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: "divider" }}>
                        <TextField
                          size="small"
                          type="number"
                          label={isGreek ? "ΥΚΩ ανά kWh από το δίκτυο" : "PSO per imported kWh"}
                          value={itemizedTariffs.ykoEurKwh ?? ""}
                          onChange={(e) => setTariffs("itemized.ykoEurKwh", e.target.value)}
                          InputLabelProps={LABEL_ON_BORDER}
                          inputProps={{ min: 0, step: "any" }}
                          sx={{ width: { xs: "100%", sm: 315 } }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                          {isGreek
                            ? "Τιμή σε €/kWh πριν από ΦΠΑ. Εφαρμόζεται μόνο στην απορρόφηση από το δίκτυο, μαζί με τυχόν φόρτιση της μπαταρίας από αυτό. Η αυτοκατανάλωση Φ/Β μειώνει τη χρέωση. Οι εξαγωγές δεν αφαιρούνται από τις εισαγόμενες kWh. Χρησιμοποιήστε τον συντελεστή της παροχής ή τεκμηριωμένη μέση τιμή αν υπάρχουν κλίμακες. Κενό ή 0 = δεν προστίθεται ΥΚΩ."
                            : "Enter EUR/kWh before VAT. Applies only to grid imports, including battery charging from the grid. PV self-consumption reduces this charge. Exports are not deducted from imported kWh. Use the connection's rate or a documented average for tiered charges. Blank or 0 = no PSO added."}
                        </Typography>
                      </Box>
                    </Paper>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ mb: 1.5 }}>
                      <TextField
                        size="small"
                        label={singleEnergyRate ? (isGreek ? "Χρέωση, όλοι οι μήνες" : "Rate for all months") : (isGreek ? "Αιχμή, όλοι οι μήνες" : "Peak for all months")}
                        type="number"
                        value={allMonthPeak}
                        onChange={(e) => setAllMonthPeak(e.target.value)}
                        InputLabelProps={LABEL_ON_BORDER}
                        inputProps={{ min: 0, step: "any" }}
                        sx={{ width: { xs: "100%", sm: 175 } }}
                      />
                      {!singleEnergyRate && <TextField
                        size="small"
                        label={isGreek ? "Εκτός αιχμής, όλοι οι μήνες" : "Off-peak for all months"}
                        type="number"
                        value={allMonthOffPeak}
                        onChange={(e) => setAllMonthOffPeak(e.target.value)}
                        InputLabelProps={LABEL_ON_BORDER}
                        inputProps={{ min: 0, step: "any" }}
                        sx={{ width: { xs: "100%", sm: 195 } }}
                      />}
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={
                          allMonthPeak === "" ||
                          (!singleEnergyRate && allMonthOffPeak === "") ||
                          !Number.isFinite(Number(allMonthPeak)) ||
                          (!singleEnergyRate && !Number.isFinite(Number(allMonthOffPeak))) ||
                          Number(allMonthPeak) < 0 ||
                          (!singleEnergyRate && Number(allMonthOffPeak) < 0)
                        }
                        onClick={() =>
                          setTariffs(
                            "itemized.monthly",
                            Object.fromEntries(MONTHS.map((month) => [month, { peak: allMonthPeak, offPeak: singleEnergyRate ? allMonthPeak : allMonthOffPeak }]))
                          )
                        }
                      >
                        {isGreek ? "Εφαρμογή στους 12 μήνες" : "Apply to all 12 months"}
                      </Button>
                    </Stack>
                    <Box sx={{ width: "100%", maxWidth: singleEnergyRate ? 560 : 840, overflowX: "auto", border: 1, borderColor: "divider", borderRadius: 2 }}>
                      <Box sx={{ minWidth: singleEnergyRate ? 420 : 690, width: "100%" }}>
                        <Box sx={{ display: "grid", gridTemplateColumns: singleEnergyRate ? "96px minmax(135px, 1fr) minmax(115px, 0.8fr)" : "96px repeat(2, minmax(135px, 1fr)) repeat(2, minmax(115px, 0.8fr))", gap: 1, px: 1.25, py: 1, bgcolor: "action.hover", borderBottom: 1, borderColor: "divider" }}>
                          {(singleEnergyRate
                            ? [isGreek ? "Μήνας" : "Month", isGreek ? "Χρέωση ενέργειας" : "Energy charge", isGreek ? "Σύνολο" : "Total"]
                            : [isGreek ? "Μήνας" : "Month", isGreek ? "Χρέωση ενέργειας αιχμής" : "Peak energy charge", isGreek ? "Χρέωση ενέργειας εκτός αιχμής" : "Off-peak energy charge", isGreek ? "Σύνολο αιχμής" : "Peak total", isGreek ? "Σύνολο εκτός αιχμής" : "Off-peak total"]).map((label) => (
                            <Typography key={label} variant="caption" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{label}<br />{label === (isGreek ? "Μήνας" : "Month") ? "" : "€/kWh"}</Typography>
                          ))}
                        </Box>
                        <Stack spacing={0}>
                          {MONTHS.map((month) => {
                            const peakCost = itemizedTariffRate(tariffs, month, "peak");
                            const offPeakCost = itemizedTariffRate(tariffs, month, "offPeak");
                            return (
                              <Box key={month} sx={{ display: "grid", gridTemplateColumns: singleEnergyRate ? "96px minmax(135px, 1fr) minmax(115px, 0.8fr)" : "96px repeat(2, minmax(135px, 1fr)) repeat(2, minmax(115px, 0.8fr))", gap: 1, alignItems: "center", px: 1.25, py: 0.4, "&:nth-of-type(even)": { bgcolor: "action.hover" } }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{month}</Typography>
                                {(singleEnergyRate ? ["peak"] : ["peak", "offPeak"]).map((band) => (
                                  <TextField
                                    key={band}
                                    size="small"
                                    variant="outlined"
                                    type="number"
                                    value={itemizedTariffs.monthly?.[month]?.[band] ?? ""}
                                    onChange={(e) => singleEnergyRate
                                      ? setTariffs(`itemized.monthly.${month}`, { peak: e.target.value, offPeak: e.target.value })
                                      : setTariffs(`itemized.monthly.${month}.${band}`, e.target.value)}
                                    sx={{ width: "100%", "& .MuiOutlinedInput-root": { borderRadius: 1, height: 32 }, "& .MuiInputBase-input": { py: 0.5, fontVariantNumeric: "tabular-nums" } }}
                                    inputProps={{
                                      min: 0,
                                      step: "any",
                                      "aria-label": `${month} ${singleEnergyRate
                                        ? (isGreek ? "χρέωση ενέργειας" : "energy charge")
                                        : band === "peak"
                                          ? (isGreek ? "χρέωση ενέργειας αιχμής" : "peak energy charge")
                                          : (isGreek ? "χρέωση ενέργειας εκτός αιχμής" : "off-peak energy charge")}`,
                                    }}
                                  />
                                ))}
                                <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                                  {peakCost ? peakCost.effective.toFixed(4) : "—"}
                                </Typography>
                                {!singleEnergyRate && <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                                  {offPeakCost ? offPeakCost.effective.toFixed(4) : "—"}
                                </Typography>}
                              </Box>
                            );
                          })}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 1.25, py: 1 }}>
                          {isGreek
                            ? "* Το σύνολο ανά εισαγόμενη kWh περιλαμβάνει χρέωση ενέργειας + λοιπές χρεώσεις + ΥΚΩ + μη ανακτήσιμο ΦΠΑ. Τα πάγια προστίθενται χωριστά στον λογαριασμό."
                            : "* Total per imported kWh includes the energy charge + other charges + PSO + non-recoverable VAT. Fixed fees are added separately to the bill."}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                  <Grid item xs={12} md={7} sx={{ display: showItemizedTariff ? "none" : undefined,
                    ...(!isDynamicTariff ? { width: "100%", maxWidth: singleEnergyRate ? 360 : 560, minWidth: 0, flex: "0 1 560px" } : {}) }}>
                    {isDynamicTariff ? (
                      <DynamicTariffFields
                        tariffs={tariffs}
                        setTariffs={setTariffs}
                        isBtm={isBtmSettlement(mergedCfg.settlementType)}
                      />
                    ) : null}
                    <Box sx={{ display: isDynamicTariff || itemizedMode ? "none" : "block" }}>
                      {!isDynamicTariff && !itemizedMode && (
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} sx={{ mb: 1.5 }}>
                        <TextField
                          size="small"
                          label={singleEnergyRate ? (isGreek ? "Ίδια τιμή όλους τους μήνες" : "Same rate every month") : (isGreek ? "Αιχμή, όλοι οι μήνες" : "Peak for all months")}
                          type="number"
                          value={allMonthTotalPeak}
                          onChange={(e) => setAllMonthTotalPeak(e.target.value.replace(",", "."))}
                          InputLabelProps={LABEL_ON_BORDER}
                          inputProps={{ min: 0, step: "any" }}
                          sx={{ width: { xs: "100%", sm: 190 } }}
                        />
                        {!singleEnergyRate && <TextField
                          size="small"
                          label={isGreek ? "Εκτός αιχμής, όλοι οι μήνες" : "Off-peak for all months"}
                          type="number"
                          value={allMonthTotalOffPeak}
                          onChange={(e) => setAllMonthTotalOffPeak(e.target.value.replace(",", "."))}
                          InputLabelProps={LABEL_ON_BORDER}
                          inputProps={{ min: 0, step: "any" }}
                          sx={{ width: { xs: "100%", sm: 195 } }}
                        />}
                        <Button
                          size="small"
                          variant="outlined"
                          disabled={
                            !/^\d*\.?\d+$|^\d+\.?$/.test(allMonthTotalPeak) ||
                            (!singleEnergyRate && !/^\d*\.?\d+$|^\d+\.?$/.test(allMonthTotalOffPeak))
                          }
                          onClick={() =>
                            setTariffs(
                              "monthly",
                              Object.fromEntries(MONTHS.map((month) => [month, {
                                peak: allMonthTotalPeak,
                                offPeak: singleEnergyRate ? allMonthTotalPeak : allMonthTotalOffPeak,
                              }]))
                            )
                          }
                        >
                          {isGreek ? "Εφαρμογή στους 12 μήνες" : "Apply to all 12 months"}
                        </Button>
                      </Stack>
                      )}
                      <Box sx={{ width: "100%", overflowX: "auto", border: 1, borderColor: "divider", borderRadius: 2 }}>
                      <Box sx={{ minWidth: singleEnergyRate ? 280 : 420 }}>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: singleEnergyRate ? "96px minmax(135px, 1fr)" : "96px repeat(2, minmax(135px, 1fr))",
                          gap: 1,
                          px: 1.25, py: 1, bgcolor: "action.hover", borderBottom: 1, borderColor: "divider",
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                          {isGreek ? "Μήνας" : "Month"}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                          {singleEnergyRate ? (isGreek ? "Τιμή όλης της ημέρας" : "All-day rate") : (isGreek ? "Τιμή αιχμής" : "Peak rate")}<br />{isGreek ? "€/kWh, τελική" : "€/kWh, final"}
                        </Typography>
                        {!singleEnergyRate && <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
                          {isGreek ? "Τιμή εκτός αιχμής" : "Off-peak rate"}<br />{isGreek ? "€/kWh, τελική" : "€/kWh, final"}
                        </Typography>}
                      </Box>
                      <Stack spacing={0}>
                        {MONTHS.map((month) => (
                          <Box
                            key={month}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: singleEnergyRate ? "96px minmax(135px, 1fr)" : "96px repeat(2, minmax(135px, 1fr))",
                            gap: 1,
                            alignItems: "center",
                            px: 1.25, py: 0.4,
                            "&:nth-of-type(even)": { bgcolor: "action.hover" },
                          }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {month}
                            </Typography>
                            <TextField
                              size="small"
                              variant="outlined"
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1, height: 32 }, "& .MuiInputBase-input": { py: 0.5, fontVariantNumeric: "tabular-nums" } }}
                              inputProps={{ min: 0, step: "any", "aria-label": `${month} ${isGreek ? "τιμή αιχμής" : "peak rate"}` }}
                              type="number"
                              value={tariffs.monthly?.[month]?.peak ?? ""}
                              onChange={(e) => {
                                const raw = e.target.value.replace(",", ".");
                                if (!/^\d*\.?\d*$/.test(raw)) return;
                                singleEnergyRate ? setTariffs(`monthly.${month}`, { peak: raw, offPeak: raw }) : setTariffs(`monthly.${month}.peak`, raw);
                              }}
                              fullWidth
                            />
                            {!singleEnergyRate && <TextField
                              size="small"
                              variant="outlined"
                              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1, height: 32 }, "& .MuiInputBase-input": { py: 0.5, fontVariantNumeric: "tabular-nums" } }}
                              inputProps={{ min: 0, step: "any", "aria-label": `${month} ${isGreek ? "τιμή εκτός αιχμής" : "off-peak rate"}` }}
                              type="number"
                              value={tariffs.monthly?.[month]?.offPeak ?? ""}
                              onChange={(e) => {
                                const raw = e.target.value.replace(",", ".");
                                if (!/^\d*\.?\d*$/.test(raw)) return;
                                setTariffs(`monthly.${month}.offPeak`, raw);
                              }}
                              fullWidth
                            />}
                          </Box>
                        ))}
                      </Stack>
                      </Box>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={showItemizedTariff ? 4 : 5}
                    sx={!isDynamicTariff ? { width: "100%", maxWidth: 400, minWidth: 0, flex: "0 1 400px" } : undefined}>
                    <Stack spacing={2}>
                      {isDynamicTariff && <Box sx={{ height: 44 }} />}
                      {showLegacyTariff && (
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                            {isGreek ? "Χρεώσεις εκτός kWh" : "Charges outside per-kWh rates"}
                          </Typography>
                          <Stack spacing={1.5}>
                            {[
                              ["fixedMonthlyEur", isGreek ? "Πάγιο προμηθευτή (€/μήνα, τελικό)" : "Supplier fixed fee (EUR/month, effective)"],
                              ["otherFixedMonthlyEur", isGreek ? "Λοιπά σταθερά (€/μήνα, τελικό)" : "Other fixed charges (EUR/month, effective)"],
                            ].map(([key, label]) => (
                              <TextField key={key} fullWidth size="small" type="number" label={label}
                                value={tariffs.legacyFixed?.[key] ?? ""}
                                onChange={event => setTariffs(`legacyFixed.${key}`, event.target.value)}
                                InputLabelProps={LABEL_ON_BORDER}
                                inputProps={{ min: 0, step: "any" }}
                                error={legacyFixedErrors.includes(key)}
                                helperText={legacyFixedErrors.includes(key)
                                  ? (isGreek ? "Δώστε ποσό από 0 και πάνω ή αφήστε κενό." : "Enter an amount of zero or more, or leave blank.") : undefined} />
                            ))}
                            <Typography variant="caption" color="text.secondary">
                              {isGreek
                                ? "Τελικά μηνιαία ποσά, μαζί με τυχόν μη ανακτήσιμο ΦΠΑ. Κενό = 0. Δεν προστίθεται άλλος ΦΠΑ και δεν διαβάζονται ποσά από την αναλυτική καταχώριση. Μην περιλάβετε χρεώσεις που έχετε ήδη ενσωματώσει στην τιμή ανά kWh ή στην ισχύ. Τα σταθερά ποσά προστίθενται και στους δύο λογαριασμούς και δεν αλλάζουν την εξοικονόμηση."
                                : "Final monthly amounts, including any non-recoverable VAT. Blank = 0. No additional VAT or amounts from itemized entry are applied. Exclude charges already included in the per-kWh or capacity rate. Fixed amounts are added to both bills and do not change savings."}
                            </Typography>
                          </Stack>
                          {capacityChargeSection}
                        </Paper>
                      )}
                      {showItemizedTariff && (
                        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
                            {isGreek ? "Χρεώσεις εκτός kWh" : "Charges outside per-kWh rates"}
                          </Typography>
                          <Stack spacing={1.5}>
                            <TextField
                              fullWidth
                              size="small"
                              // The two fixed fields follow different VAT rules; each label says its own.
                              label={isGreek ? "Πάγιο προμηθευτή (€/μήνα, χωρίς ΦΠΑ· ο ΦΠΑ προστίθεται)" : "Supplier fixed fee (EUR/month, before VAT; VAT is added)"}
                              type="number"
                              value={itemizedTariffs.fixedMonthlyEur ?? ""}
                              onChange={(e) => setTariffs("itemized.fixedMonthlyEur", e.target.value)}
                              InputLabelProps={LABEL_ON_BORDER}
                              inputProps={{ min: 0, step: "any" }}
                            />
                            <TextField
                              fullWidth
                              size="small"
                              label={isGreek ? "Λοιπά σταθερά (€/μήνα, τελικό κόστος· χωρίς προσθήκη ΦΠΑ)" : "Other fixed charges (EUR/month, final cost; no VAT added)"}
                              type="number"
                              value={itemizedTariffs.otherFixedMonthlyEur ?? ""}
                              onChange={(e) => setTariffs("itemized.otherFixedMonthlyEur", e.target.value)}
                              InputLabelProps={LABEL_ON_BORDER}
                              inputProps={{ min: 0, step: "any" }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {isGreek
                                ? "Στο πάγιο προμηθευτή δώστε το ποσό χωρίς ΦΠΑ: ο ΦΠΑ προστίθεται για το μέρος που δεν ανακτάται. Στα λοιπά σταθερά δώστε το τελικό κόστος μετά την ανάκτηση ΦΠΑ (για χρεώσεις χωρίς ΦΠΑ, το ποσό όπως είναι στον λογαριασμό), χωρίς χρεώσεις που ήδη περιλαμβάνονται ανά kWh ή στην ισχύ. Τα σταθερά ποσά μπαίνουν και στους δύο συγκρινόμενους λογαριασμούς και ακυρώνονται στην εξοικονόμηση."
                                : "Enter the supplier fee before VAT: VAT is added for the part that is not recovered. For other fixed charges, enter the final cost after VAT recovery (for charges without VAT, the amount as billed), excluding per-kWh and capacity charges. Fixed amounts appear in both compared bills and cancel out of savings."}
                            </Typography>
                          </Stack>
                          {capacityChargeSection}
                        </Paper>
                      )}
                      {!singleEnergyRate && (
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {isGreek ? "Ωράριο αιχμής" : "Peak hours"}
                        </Typography>
                      )}
                      {!singleEnergyRate && <FormControl fullWidth>
                        <InputLabel sx={technicalInputLabelSx}>Billing Scheme</InputLabel>
                        <Select
                          label="Billing Scheme"
                          value={tariffs.billingScheme || "Single zonal"}
                          onChange={(e) => setTariffs("billingScheme", e.target.value)}
                          size="small"
                          sx={compactSelectSx}
                          MenuProps={compactMenuProps}
                        >
                          {/* "Μονοζωνική" read to a Greek engineer as one price all
                              day, next to a peak and an off-peak price. These are
                              the schedules the two options actually are. */}
                          <MenuItem value="Single zonal">{isGreek ? "Ίδιο ωράριο όλο τον χρόνο" : "Same hours all year"}</MenuItem>
                          <MenuItem value="Bi-zonal">{isGreek ? "Χειμερινό και θερινό ωράριο" : "Winter and summer hours"}</MenuItem>
                          <MenuItem
                            value={DYNAMIC_SCHEME}
                            disabled={!isBtmSettlement(mergedCfg.settlementType) && !isDynamicTariff}
                          >
                            {DYNAMIC_SCHEME_LABEL}
                          </MenuItem>
                        </Select>
                      </FormControl>}
                      {isDynamicTariff || singleEnergyRate ? null : tariffs.billingScheme === "Single zonal" ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          {isGreek ? "Ώρες αιχμής, όλο τον χρόνο" : "Peak hours, all year"}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel sx={technicalInputLabelSx}>Peak start</InputLabel>
                            <Select
                              label="Peak start"
                              value={tariffs.single?.peakStart || "None"}
                              onChange={(e) =>
                                setTariffs("single.peakStart", e.target.value)
                              }
                              size="small"
                              sx={compactSelectSx}
                              MenuProps={compactMenuProps}
                            >
                                {tariffHourOptions.map((hour) => (
                                  <MenuItem key={hour} value={hour}>
                                    {hour}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <InputLabel sx={technicalInputLabelSx}>Peak end</InputLabel>
                            <Select
                              label="Peak end"
                              value={tariffs.single?.peakEnd || "None"}
                              onChange={(e) =>
                                setTariffs("single.peakEnd", e.target.value)
                              }
                              size="small"
                              sx={compactSelectSx}
                              MenuProps={compactMenuProps}
                            >
                                {tariffHourOptions.map((hour) => (
                                  <MenuItem key={hour} value={hour}>
                                    {hour}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Winter (Nov-Mar) Non-peak Hours
                        </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel sx={technicalInputLabelSx}>Day start</InputLabel>
                            <Select
                              label="Day start"
                              value={tariffs.winter?.dayStart || "None"}
                              onChange={(e) =>
                                setTariffs("winter.dayStart", e.target.value)
                              }
                              size="small"
                              sx={compactSelectSx}
                              MenuProps={compactMenuProps}
                            >
                              {tariffHourOptions.map((hour) => (
                                <MenuItem key={hour} value={hour}>
                                  {hour}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel sx={technicalInputLabelSx}>Day end</InputLabel>
                            <Select
                              label="Day end"
                              value={tariffs.winter?.dayEnd || "None"}
                              onChange={(e) =>
                                setTariffs("winter.dayEnd", e.target.value)
                              }
                              size="small"
                              sx={compactSelectSx}
                              MenuProps={compactMenuProps}
                            >
                              {tariffHourOptions.map((hour) => (
                                <MenuItem key={hour} value={hour}>
                                  {hour}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                      <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel sx={technicalInputLabelSx}>Night start</InputLabel>
                            <Select
                              label="Night start"
                              value={tariffs.winter?.nightStart || "None"}
                              onChange={(e) =>
                                setTariffs("winter.nightStart", e.target.value)
                              }
                              size="small"
                              sx={compactSelectSx}
                              MenuProps={compactMenuProps}
                            >
                              {tariffHourOptions.map((hour) => (
                                <MenuItem key={hour} value={hour}>
                                  {hour}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel sx={technicalInputLabelSx}>Night end</InputLabel>
                            <Select
                              label="Night end"
                              value={tariffs.winter?.nightEnd || "None"}
                              onChange={(e) =>
                                setTariffs("winter.nightEnd", e.target.value)
                              }
                              size="small"
                              sx={compactSelectSx}
                              MenuProps={compactMenuProps}
                            >
                              {tariffHourOptions.map((hour) => (
                                <MenuItem key={hour} value={hour}>
                                  {hour}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                          Summer (Apr-Oct) Non-peak Hours
                        </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel sx={technicalInputLabelSx}>Day start</InputLabel>
                            <Select
                              label="Day start"
                              value={tariffs.summer?.dayStart || "None"}
                              onChange={(e) =>
                                setTariffs("summer.dayStart", e.target.value)
                              }
                              size="small"
                              sx={compactSelectSx}
                              MenuProps={compactMenuProps}
                            >
                                {tariffHourOptions.map((hour) => (
                                  <MenuItem key={hour} value={hour}>
                                    {hour}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel sx={technicalInputLabelSx}>Day end</InputLabel>
                            <Select
                              label="Day end"
                              value={tariffs.summer?.dayEnd || "None"}
                              onChange={(e) =>
                                setTariffs("summer.dayEnd", e.target.value)
                              }
                              size="small"
                              sx={compactSelectSx}
                              MenuProps={compactMenuProps}
                            >
                                {tariffHourOptions.map((hour) => (
                                  <MenuItem key={hour} value={hour}>
                                    {hour}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                      </Grid>
                      <Grid container spacing={2} sx={{ mt: 2 }}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel sx={technicalInputLabelSx}>Night start</InputLabel>
                            <Select
                              label="Night start"
                              value={tariffs.summer?.nightStart || "None"}
                              onChange={(e) =>
                                setTariffs("summer.nightStart", e.target.value)
                              }
                              size="small"
                              sx={compactSelectSx}
                              MenuProps={compactMenuProps}
                            >
                                {tariffHourOptions.map((hour) => (
                                  <MenuItem key={hour} value={hour}>
                                    {hour}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel sx={technicalInputLabelSx}>Night end</InputLabel>
                            <Select
                              label="Night end"
                              value={tariffs.summer?.nightEnd || "None"}
                              onChange={(e) =>
                                setTariffs("summer.nightEnd", e.target.value)
                              }
                              size="small"
                              sx={compactSelectSx}
                              MenuProps={compactMenuProps}
                            >
                                {tariffHourOptions.map((hour) => (
                                  <MenuItem key={hour} value={hour}>
                                    {hour}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </Box>
          </Grid>
        )}
      </Grid>
    </Stack>
  </Paper>
)}
      </Stack>
      <Dialog
        open={batteryDialogOpen}
        onClose={() => setBatteryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Battery details – {batteryDialogDetails?.name || batteryDialogTarget || ""}
        </DialogTitle>
        <DialogContent dividers>
          {!batteryDialogDetails ? (
            <Typography variant="body2">Loading details…</Typography>
          ) : (
            <BatteryDiagram details={batteryDialogDetails} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatteryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={moduleDialogOpen}
        onClose={() => setModuleDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Module performance – {moduleDetails?.name || moduleName || ""}
        </DialogTitle>
        <DialogContent dividers>
          {moduleDetailsLoading && (
            <Typography variant="body2">Loading details…</Typography>
          )}
          {moduleDetailsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {moduleDetailsError}
            </Alert>
          )}
          {!moduleDetailsLoading &&
            !moduleDetailsError &&
            moduleDetails && <ModuleDiagram details={moduleDetails} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModuleDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={inverterDialogOpen}
        onClose={() => setInverterDialogOpen(false)}
        maxWidth="md"
        PaperProps={{ sx: { maxWidth: 680 } }}
        fullWidth
      >
        <DialogTitle>
          Inverter details – {inverterDetails?.name || inverterName || ""}
        </DialogTitle>
        <DialogContent dividers>
          {inverterDetailsLoading && (
            <Typography variant="body2">Loading details…</Typography>
          )}
          {inverterDetailsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {inverterDetailsError}
            </Alert>
          )}
          {!inverterDetailsLoading &&
            !inverterDetailsError &&
            inverterDetails && <EquipmentSpecTabs kind="inverter"
              chars={normalizeChars(inverterDetails.characteristics)}
              overview={<InverterDiagram details={inverterDetails} />} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInverterDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </FeasibilityShell>
  );
}
